import {
  ChartAdditionalSeries,
  CommonHelperFunctions,
  ECardinality,
  EProjectType,
  ESdChangeReason,
  ESymbolCategory,
  IAction,
  IActionDefinitionDto,
  IComponent,
  IComponentDefinition,
  IDisplayItem,
  IDisplayItemProperty,
  IKType,
  IKTypeField,
  IProperty,
  IScreenDesignDictionary,
  ISymbol
} from "@kuika/kuika-cl-model";
import _ from "lodash";
import { IScreenDesignContextState } from "../../contexts/screen-design-context/screen-design-context";
import { IApplyPropertyGridChangesToDisplayParameters } from "../../models/interface/function-parameters";
import { getActionDatasourceQualifier } from "./datasource-name-qualifier";
import type { ScreenDesignInstance } from "./designer-context";
import { setDesignerPropertySymbol } from "./designer-property-actions";
import { resolveEditTarget, type IScreenScopedInput } from "./detached-screen-session";
import { PropertyHelper } from "./property-helper";
import { buildTableFieldBindingSymbolFields, PropertySdHelper } from "./property-sd-helper";

export interface DesignerPropertyDataEditorOption {
  label: string;
  value: string;
  disabled?: boolean;
  children?: DesignerPropertyDataEditorOption[];
  /**
   * The datasource of an action option whose name another datasource also holds (KOA-18147); the
   * panel shows it in front of `label`. Kept apart from `label` and `value` because the MCP data
   * editor selects an action by exactly that `label#tag` string.
   */
  datasourceQualifier?: string;
}

export interface DesignerPropertyDataFieldEditor {
  key: string;
  label: string;
  kind: "select" | "multiselect" | "text" | "color" | "tabs";
  value?: unknown;
  options?: DesignerPropertyDataEditorOption[];
  allowClear?: boolean;
  required?: boolean;
  visible?: boolean;
  /**
   * Editors sharing the same `group` render inside one titled section card. They MUST be
   * emitted consecutively in the editors array — a group value that reappears after a gap
   * would produce two cards keyed by the same group (React duplicate-key warning).
   */
  group?: string;
  /** Section header for the group (read from the first editor of the group). */
  groupTitle?: string;
  /** Suppress this editor's own label row (e.g. a value editor that sits under a labelled tab toggle). */
  hideLabel?: boolean;
}

/**
 * Pushes the per-series chart bg-color editors into a series group.
 *
 * On web (`allowFixed === true`): a "Column / Custom" tab toggle followed by
 * either a datasource-column picker (Column) or a color picker (Custom). Both
 * modes store into the same `valueKey` field — the source-type flag tells codegen
 * whether it is a column name or a literal color.
 *
 * On native mobile (`allowFixed === false`): the toggle and the fixed-colour
 * picker are not offered; only the datasource-column picker is rendered (legacy
 * behaviour). The fixed-colour / legend-label features are web-only.
 */
function pushChartColorEditors(
  editors: DesignerPropertyDataFieldEditor[],
  label: string,
  valueKey: string,
  sourceTypeKey: string,
  sourceType: string | undefined,
  currentValue: string | undefined,
  fieldOptions: DesignerPropertyDataEditorOption[],
  group: string,
  allowFixed: boolean
): void {
  const resolvedSourceType: "column" | "fixed" = sourceType === "fixed" ? "fixed" : "column";
  if (!allowFixed) {
    // Mobile: no Column/Custom toggle, just the column picker. A stale "fixed"
    // hex value (set earlier on web) is dropped so it never shows as a bogus
    // column selection; the editor keeps its own label since no tab sits above it.
    editors.push({
      key: valueKey,
      label,
      kind: "select",
      value: resolvedSourceType === "fixed" ? undefined : currentValue,
      options: fieldOptions,
      allowClear: true,
      group
    });
    return;
  }
  // The tab toggle carries the field label ("Color"); the value editor below
  // it hides its own label so the control reads as one labelled unit.
  editors.push({
    key: sourceTypeKey,
    label,
    kind: "tabs",
    value: resolvedSourceType,
    group,
    options: [
      { label: "Column", value: "column" },
      { label: "Custom", value: "fixed" }
    ]
  });
  if (resolvedSourceType === "fixed") {
    editors.push({
      key: valueKey,
      label,
      kind: "color",
      value: currentValue,
      allowClear: true,
      hideLabel: true,
      group
    });
  } else {
    editors.push({
      key: valueKey,
      label,
      kind: "select",
      value: currentValue,
      options: fieldOptions,
      allowClear: true,
      hideLabel: true,
      group
    });
  }
}

/**
 * Pushes the per-series fixed legend (series name) editor. When left empty the
 * runtime falls back to the data field column name, preserving legacy charts.
 */
function pushChartLegendEditor(
  editors: DesignerPropertyDataFieldEditor[],
  label: string,
  legendKey: string,
  currentValue: string | undefined,
  group: string
): void {
  editors.push({ key: legendKey, label, kind: "text", value: currentValue, allowClear: true, group });
}

/** Line-style options offered per series on line charts (KOA-18057). */
const CHART_LINE_STYLE_OPTIONS: DesignerPropertyDataEditorOption[] = [
  { label: "Solid", value: "solid" },
  { label: "Dashed", value: "dashed" },
  { label: "Dotted", value: "dotted" }
];

/**
 * Pushes the per-series line-style picker (solid / dashed / dotted). Only meaningful
 * for line charts, so callers gate on the component being a LineChart. An unset value
 * falls back to "solid" both in the picker and at runtime.
 */
function pushChartLineStyleEditor(
  editors: DesignerPropertyDataFieldEditor[],
  lineStyleKey: string,
  currentValue: string | undefined,
  group: string
): void {
  editors.push({
    key: lineStyleKey,
    label: "Line Style",
    kind: "select",
    value: currentValue ?? "solid",
    options: CHART_LINE_STYLE_OPTIONS,
    group
  });
}

export interface DesignerPropertyDataEditorState {
  success: true;
  propertyToolType: string;
  actionSelector?: {
    kind: "select" | "tree";
    value?: string;
    allowClear?: boolean;
    options: DesignerPropertyDataEditorOption[];
  };
  editors: DesignerPropertyDataFieldEditor[];
  symbolPicker?: {
    enabled: boolean;
    tableLabelSourceType?: string;
  };
  infoMessage?: string;
}

interface PropertyContext {
  sdInstance: ScreenDesignInstance;
  sd: IScreenDesignContextState;
  /**
   * The screen every write in this module lands on — carried, not re-derived.
   *
   * `sd` is already resolved to the right screen, but each write leaves through
   * `setDesignerPropertySymbol(context.sdInstance, …)`, which resolves the target ITSELF from its
   * own input. Without this field those calls named no screen, so a data binding was READ off a
   * task screen and WRITTEN to whatever screen the designer had open — silently, onto a component
   * that merely shares an id. KOA-18431: the workflow DSL's `DATA` / `DATAFIELD` lines are the
   * caller that made the gap reachable.
   */
  screenId?: string | null;
  di: IDisplayItem;
  property: IProperty;
  propertyToolType: string;
  propertyHelper: PropertyHelper;
  propertySdHelper: PropertySdHelper;
  parentDi?: IDisplayItem;
}

const TREE_ACTION_TOOL_TYPES = new Set<string>([
  "Options",
  "TableOptions",
  "GoogleMapsOptions",
  "TreeOptions",
  "DirectoryOptions",
  "KanbanBoardLaneOptions",
  "KanbanBoardCardOptions",
  "NodeTreeNodeOptions",
  "ChartOptions"
]);

const SIMPLE_ACTION_TOOL_TYPES = new Set<string>([
  "CalendarOptions",
  "MenuOptions",
  "BreadcrumbOptions",
  "RadialChartOptions",
  "RadialChartLevelOptions",
  "MixedChartOptions",
  "MixedChartLineOptions",
  "MixedChartPointOptions",
  "MixedChartBarOptions",
  "TimelineOptions",
  "ChatOptions"
]);

function getScreenDictionary(sd: IScreenDesignContextState): IScreenDesignDictionary {
  const fallback = (
    sd as unknown as { screenProvider?: { state?: { screenDesignDictionary?: IScreenDesignDictionary } } }
  )?.screenProvider?.state?.screenDesignDictionary;
  return sd?.screenDesignDictionary || fallback || ({} as IScreenDesignDictionary);
}

function applyPropertyGridChange(
  sd: IScreenDesignContextState,
  params: IApplyPropertyGridChangesToDisplayParameters,
  changeReason: ESdChangeReason
): void {
  const applyFn = sd.applyPropertyGridChangesToDisplay;
  applyFn.call(sd, params, changeReason);
}

function resolvePropertyContext(
  sdInstance: ScreenDesignInstance,
  input: { kuikaId?: string; propertyName?: string; screenId?: string | null }
): PropertyContext | { error: string } {
  // `resolveEditTarget`, not `getValues`: the workflow screen builder edits a task screen that is
  // NOT open, and every read below (dictionary, component definition, defined actions) plus the
  // write at the end has to be about that screen. Without a `screenId` this is the open screen.
  const sd = resolveEditTarget(sdInstance, input?.screenId);
  const kuikaId = input?.kuikaId;
  const propertyName = input?.propertyName;

  if (!kuikaId) return { error: "kuikaId is required" };
  if (!propertyName) return { error: "propertyName is required" };

  const dict = getScreenDictionary(sd);
  const di = dict[parseInt(kuikaId, 10)] as IDisplayItem | undefined;
  if (!di?.item) {
    // Provide actionable suggestions by listing existing components on the screen
    const hints: string[] = [];
    const SKIP_TYPES = new Set(["Row", "Col", "Unknown", "HTML_DIV", "TableHeader", "TableRow"]);
    for (const key of Object.keys(dict).slice(0, 300)) {
      const c = dict[parseInt(key, 10)] as IDisplayItem | undefined;
      if (c?.item?.name && !SKIP_TYPES.has(c.item.name) && c.id != null) {
        const fn = c.friendlyName ? `"${c.friendlyName}" ` : "";
        hints.push(`${fn}${c.item.name}:${c.id}`);
        if (hints.length >= 8) break;
      }
    }
    let msg = `Component with kuikaId "${kuikaId}" not found`;
    if (hints.length > 0) {
      msg += `. Available components: ${hints.join(", ")}`;
    }
    return { error: msg };
  }

  const propertyHelper = new PropertyHelper();
  const propertySdHelper = new PropertySdHelper(() => sd);
  const property =
    propertySdHelper.getPropertiesByDisplayItem(di)?.find((candidate: IProperty) => candidate.name === propertyName) ||
    (di.item.properties?.find((candidate) => candidate.name === propertyName) as IProperty | undefined);
  if (!property) {
    return { error: `Property "${propertyName}" not found on component ${di.item.name || kuikaId}` };
  }

  let propertyToolType: string = propertyHelper.calculatePropertyToolType(di, property);
  const parentDi = di.parentId ? dict[di.parentId] : undefined;

  if (
    parentDi &&
    di &&
    (propertyToolType === "Options" || propertyToolType === "TableOptions") &&
    isNestedDataRepeaterOptionsProperty(sd, di)
  ) {
    propertyToolType = "NestedTableOptions";
  }

  return {
    sdInstance,
    sd,
    screenId: input?.screenId,
    di,
    property,
    propertyToolType,
    propertyHelper,
    propertySdHelper,
    parentDi
  };
}

function isNestedDataRepeaterOptionsProperty(sd: IScreenDesignContextState, di: IDisplayItem): boolean {
  if (!di?.item?.name) return false;

  const componentDefinition = sd?.getComponentDefinition?.(di.item) as IComponentDefinition | undefined;
  const dataRepeaterParent = getDirectDataRepeaterParent(sd, di);
  return !!componentDefinition?.isDataRepeater && dataRepeaterParent !== undefined;
}

function getDirectDataRepeaterParent(sd: IScreenDesignContextState, di: IDisplayItem): IDisplayItem | undefined {
  if (!di.parentId) {
    return undefined;
  }

  const dict = getScreenDictionary(sd);
  const parentDi = dict[di.parentId] as IDisplayItem | undefined;
  if (!parentDi) {
    return undefined;
  }

  const componentDefinition = sd?.getComponentDefinition?.(parentDi.item) as IComponentDefinition | undefined;
  if (
    componentDefinition?.name !== "TableTemplateColumn" &&
    componentDefinition?.name !== "TableColumn" &&
    componentDefinition?.name !== "EditableTableColumn" &&
    componentDefinition?.name !== "TableDefaultColumn" &&
    componentDefinition?.name !== "TableRow" &&
    componentDefinition?.name !== "ReportTableColumn" &&
    componentDefinition?.name !== "ReportTableRow" &&
    componentDefinition?.name !== "ContentMenu" &&
    componentDefinition?.isDataRepeater
  ) {
    return parentDi;
  }

  return getDirectDataRepeaterParent(sd, parentDi);
}

function getSelectedDataAction(sd: IScreenDesignContextState, symbol: ISymbol): string | undefined {
  if (symbol?.isFixed) {
    return "Fixed Option List";
  }

  const selectedActionId = symbol?.actionDefinitionId;
  const selectedActionDef: IActionDefinitionDto | undefined = sd?.actionDefinitions?.find(
    (actionDef: IActionDefinitionDto) => actionDef.id === selectedActionId
  );

  if (symbol?.actionDefinitionObjectPath) {
    if (symbol.resultActionId) {
      const action = sd?.getDefinedActions?.().find((candidate: IAction) => candidate.id === symbol.resultActionId);
      if (action) {
        return getActionLabel(action);
      }
    }

    return symbol.actionDefinitionObjectPath;
  }

  return symbol?.actionDefinitionObjectPath !== undefined ? symbol.actionDefinitionObjectPath : selectedActionDef?.name;
}

function getActionLabel(action: IAction): string {
  return action.tag ? `${action.label}#${action.tag}` : action.label;
}

/** The option as it was, plus its datasource while another datasource holds the same action name. */
function qualifyActionOption(
  sd: IScreenDesignContextState,
  actionDef: IActionDefinitionDto | undefined,
  option: DesignerPropertyDataEditorOption
): DesignerPropertyDataEditorOption {
  const datasourceQualifier: string | undefined = getActionDatasourceQualifier(actionDef, sd?.actionDefinitions);
  return datasourceQualifier ? { ...option, datasourceQualifier } : option;
}

function pushToActionList(actionList: IAction[], action: IAction): IAction[] {
  if (actionList.some((candidate) => candidate.id === action.id)) {
    return actionList;
  }

  return [...actionList, action];
}

function getNestedActions(action: IAction): IAction[] {
  let actionList: IAction[] = [];

  if (action.ifThenElseData) {
    if (action.ifThenElseData.ifActions) {
      action.ifThenElseData.ifActions.forEach((nestedAction) => {
        if (nestedAction.ifThenElseData) {
          actionList = [...actionList, ...getNestedActions(nestedAction)];
        } else {
          actionList = pushToActionList(actionList, nestedAction);
        }
      });
    }

    if (action.ifThenElseData.elseActions) {
      action.ifThenElseData.elseActions.forEach((nestedAction) => {
        if (nestedAction.ifThenElseData) {
          actionList = [...actionList, ...getNestedActions(nestedAction)];
        } else {
          actionList = pushToActionList(actionList, nestedAction);
        }
      });
    }
  } else if (action.recurringActionsData && action.recurringActionsData.recurringActions) {
    return actionList;
  } else {
    actionList = pushToActionList(actionList, action);
  }

  return actionList;
}

function getRuntimeActionDefinition(sd: IScreenDesignContextState, action: IAction): IActionDefinitionDto | undefined {
  let actionDef = sd?.actionDefinitions?.find(
    (candidate: IActionDefinitionDto) => candidate.id === action.actionDefinitionId
  ) as IActionDefinitionDto | undefined;

  if (actionDef?.name === "GetFromBag" || actionDef?.name === "FetchFromCache") {
    actionDef = _.cloneDeep(actionDef);
    actionDef.returnTypeModel = _.cloneDeep(action.returnTypeModel) as IKType;
    actionDef.returnTypeName = action.returnType as string;
    actionDef.returnCardinality = action.returnCardinality as ECardinality;
  }

  return actionDef;
}

function getChildOptionNodes(elements: IKTypeField[], path: string): DesignerPropertyDataEditorOption[] {
  if (!elements || elements.length === 0) {
    return [];
  }

  return elements.map((element) => {
    const currentPath = `${path}.${element.name}`;
    if (element.isList === true) {
      return {
        label: element.name,
        value: currentPath
      };
    }

    return {
      label: element.name,
      value: currentPath,
      disabled: true,
      children: getChildOptionNodes(element.elementTypeModel?.elements ?? [], currentPath)
    };
  });
}

function getTreeActionOptions(
  sd: IScreenDesignContextState,
  includeFixedOptionList: boolean
): DesignerPropertyDataEditorOption[] {
  let actions: IAction[] = sd?.getDefinedActions?.() || [];
  actions = actions.filter((thing, index, arr) => {
    // IfThenElse containers are expanded into their nested actions below, so a
    // label-based dedup (they are all labeled "IfThenElse") would drop every
    // container after the first and hide its nested list actions (KOA-17919).
    // Dedup containers by instance id; the expanded entries are deduped by
    // value when pushed.
    if (thing.ifThenElseData) {
      return arr.findIndex((candidate) => candidate.id === thing.id) === index;
    }
    return arr.findIndex((candidate) => candidate.label === thing.label && candidate.tag === thing.tag) === index;
  });
  actions = actions.sort((left, right) => (left?.label ? left.label.localeCompare(right?.label || "") : -1));

  const options: DesignerPropertyDataEditorOption[] = [];
  const seenOptionValues: Set<string> = new Set<string>();
  if (includeFixedOptionList) {
    options.push({ label: "Fixed Option List", value: "Fixed Option List" });
  }

  const pushActionOption = (action: IAction, actionDef: IActionDefinitionDto | undefined): void => {
    const optionValue: string = getActionLabel(action);
    if (seenOptionValues.has(optionValue)) {
      return;
    }
    seenOptionValues.add(optionValue);

    if (actionDef?.returnCardinality === ECardinality.Many || actionDef?.returnTypeName?.toLowerCase() === "any") {
      options.push(
        qualifyActionOption(sd, actionDef, {
          label: optionValue,
          value: optionValue
        })
      );
      return;
    }

    options.push(
      qualifyActionOption(sd, actionDef, {
        label: optionValue,
        value: optionValue,
        disabled: true,
        children: getChildOptionNodes(actionDef?.returnTypeModel?.elements ?? [], action.label)
      })
    );
  };

  actions.forEach((action) => {
    const actionDef = getRuntimeActionDefinition(sd, action);
    if (actionDef?.name === "IfThenElse" || actionDef?.name === "RecurringActions") {
      getNestedActions(action).forEach((nestedAction) => {
        pushActionOption(nestedAction, getRuntimeActionDefinition(sd, nestedAction));
      });
      return;
    }

    pushActionOption(action, actionDef);
  });

  return options;
}

function getSimpleActionOptions(sd: IScreenDesignContextState): DesignerPropertyDataEditorOption[] {
  const actions: IAction[] = sd?.getDefinedActions?.() || [];
  const options: DesignerPropertyDataEditorOption[] = [];

  actions.forEach((action) => {
    const actionDef = getRuntimeActionDefinition(sd, action);
    if (actionDef?.returnCardinality !== ECardinality.Many) {
      return;
    }

    options.push(
      qualifyActionOption(sd, actionDef, {
        label: getActionLabel(action),
        value: getActionLabel(action)
      })
    );
  });

  return options;
}

function createFieldOptions(
  fields: IKTypeField[],
  serializer?: (field: IKTypeField) => string
): DesignerPropertyDataEditorOption[] {
  return fields.map((field) => ({
    label: field.name,
    value: serializer ? serializer(field) : field.name
  }));
}

function getActionFieldOptions(context: PropertyContext): DesignerPropertyDataEditorOption[] {
  if (context.propertyToolType === "Options" && context.property.symbol?.isFixed === true) {
    return [
      { label: "Key", value: "Key" },
      { label: "Text", value: "Text" }
    ];
  }

  let fields = context.propertySdHelper.getActionFieldsBySymbol(context.property.symbol);
  fields = fields.sort((left, right) => (left.name > right.name ? 1 : -1));
  return createFieldOptions(fields);
}

function getTableFieldOptions(context: PropertyContext): IKTypeField[] {
  return (
    context.propertySdHelper.getTableActionFieldsByKuikaId(
      context.di.id!.toString(),
      context.propertyToolType === "NestedTableOptions"
    ) || []
  );
}

function serializeTableFieldOption(field: IKTypeField): string {
  return JSON.stringify(field);
}

function getSerializedSelectedTableField(symbol: ISymbol, tableFields: IKTypeField[]): string | undefined {
  const selectedFieldName = symbol?.fieldName;
  if (!selectedFieldName) {
    return undefined;
  }

  const selectedField = tableFields.find((field: IKTypeField) => {
    const fieldPath = field?.prefix ? `${field.prefix}.${field.name}` : field.name;
    return fieldPath === selectedFieldName || field.name === selectedFieldName;
  });

  return selectedField ? serializeTableFieldOption(selectedField) : undefined;
}

function getValuePropEditorState(context: PropertyContext): DesignerPropertyDataEditorState | null {
  const property = context.property;
  if (
    property.isValueProp !== true &&
    property.name !== "dynamicFontColor" &&
    property.name !== "dynamicBackGroundColor" &&
    property.name !== "dynamicBgImage" &&
    context.propertyToolType !== "NestedTableOptions"
  ) {
    return null;
  }

  const tableFields = getTableFieldOptions(context);
  if (!tableFields.length) {
    if (context.propertyToolType === "NestedTableOptions") {
      const parentSymbol = context.propertySdHelper.getTableOptionPropSymbolByDi(context.di);
      const isParentBoundToAction = !!parentSymbol?.actionDefinitionId;
      const infoMessage = isParentBoundToAction
        ? "The parent table's action returns no nested array (list) fields."
        : "Bind the parent table's dataSource to an action first — then this inner table can bind to a nested array (sub-collection) field exposed by that action.";
      return {
        success: true,
        propertyToolType: context.propertyToolType,
        editors: [],
        infoMessage
      };
    }
    return null;
  }

  const parentName = context.parentDi?.item?.name;
  const selectedSourceType = property.symbol?.tableLabelSourceType || "tableBinding";
  const editors: DesignerPropertyDataFieldEditor[] = [
    {
      key: "tableLabelSourceType",
      label: "Select Value Type",
      kind: "select",
      value: selectedSourceType,
      options: [
        { label: "Table Fields", value: "tableBinding" },
        { label: "Symbol Picker", value: "symbolPicker" }
      ]
    }
  ];

  if (selectedSourceType === "tableBinding") {
    editors.push({
      key: "tableFieldBinding",
      label: context.propertyToolType === "NestedTableOptions" ? "Field to bind" : "Field to display",
      kind: "select",
      value: getSerializedSelectedTableField(property.symbol || {}, tableFields),
      options: createFieldOptions(tableFields, serializeTableFieldOption)
    });

    if (parentName === "EditableTableColumn" || parentName === "TableDefaultColumn") {
      const rendererNames = CommonHelperFunctions.getTableColumnEditModeRenderer(property.symbol?.fieldType);
      editors.push({
        key: "tableEditModeRenderer",
        label: "Edit Mode Component",
        kind: "select",
        value: property.symbol?.tableEditModeRenderer,
        options: rendererNames.map((name: string) => ({ label: name, value: name }))
      });
    }

    editors.push({
      key: "valuePlaceholderText",
      label: "Design Placeholder",
      kind: "text",
      value: property.symbol?.valuePlaceholderText
    });
  }

  return {
    success: true,
    propertyToolType: context.propertyToolType,
    editors,
    symbolPicker: {
      enabled: selectedSourceType === "symbolPicker",
      tableLabelSourceType: selectedSourceType
    }
  };
}

/**
 * Value-bindings that STYLE a component rather than render its content. They are
 * persisted as `[datafield:...]` symbols exactly like a content binding, but a
 * column's sort/search field must never resolve to one of them — you cannot
 * meaningfully sort a column by the field that drives a Panel's background
 * colour. These are therefore excluded from the `defaultDataField` options.
 * KOA-17569.
 */
const STYLE_ONLY_DATAFIELD_PROPERTIES: ReadonlySet<string> = new Set([
  "dynamicBackGroundColor",
  "dynamicFontColor",
  "dynamicBgImage"
]);

/**
 * `[datafield:...]` bindings the table runtime ignores when inferring a column's
 * data field (mirrors base-table.tsx's `processChildren` exclusions). Keeping the
 * designer aligned with the runtime keeps visibility and options consistent with
 * what actually drives sorting and filtering. KOA-17569.
 */
const NON_CONTENT_DATAFIELD_PROPERTIES: ReadonlySet<string> = new Set(["visExField1", "visExField2", "tooltip"]);

export interface ColumnDataFieldScan {
  /**
   * Distinct field names bound anywhere in the column via `[datafield:...]`,
   * excluding the runtime-ignored keys but INCLUDING styling bindings. Mirrors
   * how the table runtime decides a column is ambiguous and therefore needs
   * `defaultDataField` to disambiguate its sort/search field.
   */
  fieldNames: string[];
  /**
   * Distinct field names from CONTENT bindings only (styling bindings removed) —
   * the fields a user may legitimately pick as the column's default data field.
   */
  contentFieldNames: string[];
}

function isDataFieldSymbol(symbol: ISymbol | undefined): boolean {
  return typeof symbol?.fixedValue === "string" && symbol.fixedValue.includes("datafield");
}

/**
 * Walk a TableTemplateColumn's descendants (any depth) and collect the table
 * fields its children are bound to, separating content bindings from styling-only
 * bindings. Shared by the property visibility gate (property-form) and the
 * `defaultDataField` options builder so the two never disagree. KOA-17569.
 */
export function scanColumnDataFields(children: IDisplayItem[] | undefined, maxDepth: number = 10): ColumnDataFieldScan {
  const fieldNames: Set<string> = new Set<string>();
  const contentFieldNames: Set<string> = new Set<string>();

  function walk(nodes: IDisplayItem[] | undefined, depth: number): void {
    if (!nodes || depth <= 0) {
      return;
    }
    nodes.forEach((node: IDisplayItem) => {
      node?.item?.properties?.forEach((property: IDisplayItemProperty) => {
        const fieldName: string | undefined = property?.symbol?.fieldName;
        if (!fieldName || !isDataFieldSymbol(property?.symbol)) {
          return;
        }
        if (NON_CONTENT_DATAFIELD_PROPERTIES.has(property.name)) {
          return;
        }
        fieldNames.add(fieldName);
        if (!STYLE_ONLY_DATAFIELD_PROPERTIES.has(property.name)) {
          contentFieldNames.add(fieldName);
        }
      });
      walk(node?.children, depth - 1);
    });
  }

  walk(children, maxDepth);
  return { fieldNames: [...fieldNames], contentFieldNames: [...contentFieldNames] };
}

/**
 * `defaultDataField` is the manual disambiguator the table runtime falls back to
 * when a column binds two or more DIFFERENT fields and cannot auto-pick one
 * (base-table.tsx). It is only meaningful — and only shown — when the column has
 * 2+ distinct bound fields (styling bindings included, matching the runtime) AND
 * at least one selectable content field. A single bound field is auto-resolved by
 * the runtime, so the property stays hidden. KOA-17569.
 */
export function shouldShowDefaultDataField(scan: ColumnDataFieldScan): boolean {
  return scan.fieldNames.length >= 2 && scan.contentFieldNames.length >= 1;
}

function getDefaultDataFieldEditorState(context: PropertyContext): DesignerPropertyDataEditorState | null {
  if (context.property.name !== "defaultDataField") {
    return null;
  }

  const scan: ColumnDataFieldScan = scanColumnDataFields(context.di.children);
  if (!shouldShowDefaultDataField(scan)) {
    return null;
  }

  const actionFields = context.propertySdHelper.getTableActionFieldsByKuikaId(context.di.id!.toString(), false) || [];
  const options = actionFields
    .filter((field) => scan.contentFieldNames.includes(field.name))
    .map((field) => ({ label: field.name, value: field.name }));

  if (!options.length) {
    return null;
  }

  return {
    success: true,
    propertyToolType: context.propertyToolType,
    editors: [
      {
        key: "fixedValue",
        label: "Field to use as default",
        kind: "select",
        value: context.property.symbol?.fixedValue,
        allowClear: true,
        options
      }
    ]
  };
}

function getRowKeyEditorState(context: PropertyContext): DesignerPropertyDataEditorState | null {
  if (context.property.name !== "rowKey") {
    return null;
  }

  const datasourceSymbol = context.di.item.properties.find((property) => property.name === "dataSource")?.symbol;
  let actionFields = context.propertySdHelper.getActionFieldsBySymbol(datasourceSymbol);
  actionFields = actionFields.sort((left, right) => (left.name > right.name ? 1 : -1));
  if (!actionFields.length) {
    return null;
  }

  return {
    success: true,
    propertyToolType: context.propertyToolType,
    editors: [
      {
        key: "fixedValue",
        label: "Row Key",
        kind: "select",
        value: context.property.symbol?.fixedValue,
        allowClear: true,
        options: createFieldOptions(actionFields)
      }
    ]
  };
}

function buildEditors(
  context: PropertyContext,
  actionFieldOptions: DesignerPropertyDataEditorOption[]
): DesignerPropertyDataFieldEditor[] {
  const symbol = context.property.symbol || ({} as ISymbol);
  const targetComponentName = context.di.item?.name;
  const editors: DesignerPropertyDataFieldEditor[] = [];

  // Fixed (static) chart colour and per-series legend labels are web-only. They
  // are shown on web/desktop projects, and on a "web screen" embedded inside a
  // mobile project (which renders via React); they are hidden on native mobile
  // screens. Mirrors the gate used in unified-styling-selector and
  // designer-screen-query-actions.
  const isWebContext: boolean =
    context.sd?.project?.projectType === EProjectType.Desktop || (context.sd?.getIsWebScreen?.() ?? false);

  // Per-series line style (solid/dashed/dotted) only affects line-mode charts, and it
  // is a web-only runtime feature — the mobile (iOS/Android) generators don't emit
  // `borderDash`. So the picker is offered on a LineChart in a web context only; on
  // native mobile it stays hidden (mobile chart support is handled separately). KOA-18057.
  const showLineStyle: boolean = isWebContext && targetComponentName === "LineChart";

  // KOA-18299 — option grouping and selected-tag colouring are React-only features of the shared
  // KSelectBox runtime; the Android/iOS generators never read extra Options symbol fields (they use
  // fieldToDisplay/fieldToUseAsKey only), so both pickers follow the same web-only gate as the line
  // style above. Mobile parity is tracked separately by KOA-18300 / KOA-18301.
  // The "Options" tool type is shared with Pagination/Table/RadioGroup/AutoComplete, hence the
  // component-name narrowing. Colour applies to tags, which only a multi-select renders.
  const showOptionGroupField: boolean =
    isWebContext && ["SelectBox", "MultiSelect"].includes(targetComponentName || "");
  const showOptionTagColorField: boolean = isWebContext && targetComponentName === "MultiSelect";

  switch (context.propertyToolType) {
    case "Options": {
      // A fixed list is always Key/Text rows; the Date (KOA-18129 specific days) reads Key, then Text.
      const hideEditors =
        getSelectedDataAction(context.sd, symbol) === "Fixed Option List" &&
        ["SelectBox", "MultiSelect", "Pagination", "Table", "RadioGroup", "Date"].includes(targetComponentName || "");
      editors.push({
        key: "fieldToDisplay",
        label: "Field to display",
        kind: "select",
        value: symbol.fieldToDisplay,
        options: actionFieldOptions,
        visible: !hideEditors
      });
      editors.push({
        key: "fieldToUseAsKey",
        label: "Field to Use as Key",
        kind: "select",
        value: symbol.fieldToUseAsKey,
        options: actionFieldOptions,
        visible: !hideEditors
      });
      editors.push({
        key: "fieldToUseAsGroup",
        label: "Field to Use as Group",
        kind: "select",
        value: symbol.fieldToUseAsGroup,
        options: actionFieldOptions,
        allowClear: true,
        visible: !hideEditors && showOptionGroupField
      });
      editors.push({
        key: "fieldToUseAsTagColor",
        label: "Field to Use as Tag Color",
        kind: "select",
        value: symbol.fieldToUseAsTagColor,
        options: actionFieldOptions,
        allowClear: true,
        visible: !hideEditors && showOptionTagColorField
      });
      if (symbol.isFixed === true) {
        editors.push({
          key: "fixedValue",
          label: "Fixed Option List Value",
          kind: "text",
          value: symbol.fixedValue,
          visible: true
        });
      }
      break;
    }

    case "TreeOptions":
      editors.push(
        {
          key: "fieldToDisplay",
          label: "Field to display",
          kind: "select",
          value: symbol.fieldToDisplay,
          options: actionFieldOptions
        },
        {
          key: "fieldToUseAsKey",
          label: "Field to Use as Key",
          kind: "select",
          value: symbol.fieldToUseAsKey,
          options: actionFieldOptions
        },
        {
          key: "fieldToUseAsParent",
          label: "Field to Use as Parent",
          kind: "select",
          value: symbol.fieldToUseAsParent,
          options: actionFieldOptions
        },
        {
          key: "fieldToUseAsChecked",
          label: "Field to Use as Checked",
          kind: "select",
          value: symbol.fieldToUseAsChecked,
          options: actionFieldOptions
        },
        // KOA-13797: per-node icon (Material name or image URL column), colour, and the extra
        // display columns. Columns is stored comma-joined like chartMultiTooltipContentField.
        {
          key: "fieldToUseAsIcon",
          label: "Icon Field",
          kind: "select",
          value: symbol.fieldToUseAsIcon,
          options: actionFieldOptions,
          allowClear: true
        },
        {
          key: "fieldToUseAsColor",
          label: "Color Field",
          kind: "select",
          value: symbol.fieldToUseAsColor,
          options: actionFieldOptions,
          allowClear: true
        },
        {
          key: "fieldsToUseAsColumns",
          label: "Columns",
          kind: "multiselect",
          value: symbol.fieldsToUseAsColumns ? symbol.fieldsToUseAsColumns.split(",").filter(Boolean) : [],
          options: actionFieldOptions,
          allowClear: true
        }
      );
      break;

    case "DirectoryOptions":
      editors.push(
        {
          key: "fieldToDisplay",
          label: "Field to display",
          kind: "select",
          value: symbol.fieldToDisplay,
          options: actionFieldOptions
        },
        {
          key: "fieldToUseAsKey",
          label: "Field to Use as Key",
          kind: "select",
          value: symbol.fieldToUseAsKey,
          options: actionFieldOptions
        },
        {
          key: "fieldToUseAsParent",
          label: "Field to Use as Parent",
          kind: "select",
          value: symbol.fieldToUseAsParent,
          options: actionFieldOptions
        }
      );
      break;

    case "GoogleMapsOptions":
      editors.push(
        {
          key: "fieldToLatitude",
          label: "Field to Latitude",
          kind: "select",
          value: symbol.fieldToLatitude,
          options: actionFieldOptions
        },
        {
          key: "fieldToLongitude",
          label: "Field to Longitude",
          kind: "select",
          value: symbol.fieldToLongitude,
          options: actionFieldOptions
        }
      );
      break;

    case "KanbanBoardLaneOptions":
      editors.push(
        {
          key: "KanbanBoardLaneOptions.laneId",
          label: "Field to Lane ID",
          kind: "select",
          value: symbol.KanbanBoardLaneOptions?.laneId,
          options: actionFieldOptions
        },
        {
          key: "KanbanBoardLaneOptions.laneIndex",
          label: "Field to Lane Index",
          kind: "select",
          value: symbol.KanbanBoardLaneOptions?.laneIndex,
          options: actionFieldOptions
        }
      );
      break;

    case "KanbanBoardCardOptions":
      editors.push(
        {
          key: "KanbanBoardCardOptions.cardId",
          label: "Field to Card ID",
          kind: "select",
          value: symbol.KanbanBoardCardOptions?.cardId,
          options: actionFieldOptions
        },
        {
          key: "KanbanBoardCardOptions.cardIndex",
          label: "Field to Card Index",
          kind: "select",
          value: symbol.KanbanBoardCardOptions?.cardIndex,
          options: actionFieldOptions
        },
        {
          key: "KanbanBoardCardOptions.cardLaneId",
          label: "Field to Card Lane ID",
          kind: "select",
          value: symbol.KanbanBoardCardOptions?.cardLaneId,
          options: actionFieldOptions
        },
        {
          key: "KanbanBoardCardOptions.cardSortKey",
          label: "Field to Card Sort Key",
          kind: "select",
          value: symbol.KanbanBoardCardOptions?.cardSortKey,
          options: actionFieldOptions,
          allowClear: true
        }
      );
      break;

    case "NodeTreeNodeOptions":
      editors.push(
        {
          key: "NodeTreeNodeOptions.nodeTreeNodeId",
          label: "Field Node ID",
          kind: "select",
          value: symbol.NodeTreeNodeOptions?.nodeTreeNodeId,
          options: actionFieldOptions
        },
        {
          key: "NodeTreeNodeOptions.nodeTreeNodeParentId",
          label: "Field Node Parent ID",
          kind: "select",
          value: symbol.NodeTreeNodeOptions?.nodeTreeNodeParentId,
          options: actionFieldOptions
        },
        {
          key: "NodeTreeNodeOptions.nodeTreeConnectionHeight",
          label: "Field Connection Height",
          kind: "select",
          value: symbol.NodeTreeNodeOptions?.nodeTreeConnectionHeight,
          options: actionFieldOptions,
          allowClear: true
        },
        {
          key: "NodeTreeNodeOptions.nodeTreeNodeWidth",
          label: "Field Node Width",
          kind: "select",
          value: symbol.NodeTreeNodeOptions?.nodeTreeNodeWidth,
          options: actionFieldOptions,
          allowClear: true
        },
        {
          key: "NodeTreeNodeOptions.nodeTreeNodeHeight",
          label: "Field Node Height",
          kind: "select",
          value: symbol.NodeTreeNodeOptions?.nodeTreeNodeHeight,
          options: actionFieldOptions,
          allowClear: true
        },
        {
          key: "NodeTreeNodeOptions.nodeTreeNodeBackgroundColor",
          label: "Field Node Background Color",
          kind: "select",
          value: symbol.NodeTreeNodeOptions?.nodeTreeNodeBackgroundColor,
          options: actionFieldOptions,
          allowClear: true
        },
        {
          key: "NodeTreeNodeOptions.nodeTreeCompactChildView",
          label: "Field Compact Child View",
          kind: "select",
          value: symbol.NodeTreeNodeOptions?.nodeTreeCompactChildView,
          options: actionFieldOptions,
          allowClear: true
        },
        {
          key: "NodeTreeNodeOptions.nodeTreeCrossLinkId",
          label: "Field Cross Link To Node Id",
          kind: "select",
          value: symbol.NodeTreeNodeOptions?.nodeTreeCrossLinkId,
          options: actionFieldOptions,
          allowClear: true
        },
        {
          key: "NodeTreeNodeOptions.nodeTreeCrossLinkWidth",
          label: "Field Cross Link Width",
          kind: "select",
          value: symbol.NodeTreeNodeOptions?.nodeTreeCrossLinkWidth,
          options: actionFieldOptions,
          allowClear: true
        },
        {
          key: "NodeTreeNodeOptions.nodeTreeCrossLinkColor",
          label: "Field Cross Link Color",
          kind: "select",
          value: symbol.NodeTreeNodeOptions?.nodeTreeCrossLinkColor,
          options: actionFieldOptions,
          allowClear: true
        }
      );
      break;

    case "ChartOptions":
      editors.push({
        key: "chartDataDisplayLabel",
        label: "Data Label",
        kind: "select",
        value: symbol.chartDataDisplayLabel,
        options: actionFieldOptions,
        required: true
      });
      if (symbol.chartDataDisplayLabel) {
        editors.push(
          {
            key: "chartDataLabel",
            label: "Tooltip Custom Title Data Label",
            kind: "select",
            value: symbol.chartDataLabel,
            options: actionFieldOptions,
            allowClear: true
          },
          {
            key: "chartTooltipFooterDataLabel",
            label: "Tooltip Custom Content Data Label",
            kind: "select",
            value: symbol.chartTooltipFooterDataLabel,
            options: actionFieldOptions,
            allowClear: true
          },
          {
            key: "chartMultiTooltipContentField",
            label: "Multi Tooltip Content Fields",
            kind: "multiselect",
            value: symbol.chartMultiTooltipContentField
              ? symbol.chartMultiTooltipContentField.split(",").filter(Boolean)
              : [],
            options: actionFieldOptions,
            allowClear: true
          }
        );

        if (targetComponentName === "BarChart") {
          editors.push({
            key: "chartTargetLineField",
            label: "Target Line Field",
            kind: "select",
            value: symbol.chartTargetLineField,
            options: actionFieldOptions,
            allowClear: true
          });
        }

        editors.push({
          key: "chartFirstDataField",
          label: "Data Field",
          kind: "select",
          value: symbol.chartFirstDataField,
          options: actionFieldOptions,
          required: true,
          group: "chartSeries1",
          groupTitle: "Series 1"
        });

        if (symbol.chartFirstDataField) {
          pushChartColorEditors(
            editors,
            "Color",
            "chartFirstDataBgColor",
            "chartFirstDataBgColorSourceType",
            symbol.chartFirstDataBgColorSourceType,
            symbol.chartFirstDataBgColor,
            actionFieldOptions,
            "chartSeries1",
            isWebContext
          );
          if (isWebContext) {
            pushChartLegendEditor(
              editors,
              "Column Display Name",
              "chartFirstDataLegendLabel",
              symbol.chartFirstDataLegendLabel,
              "chartSeries1"
            );
          }
          if (showLineStyle) {
            pushChartLineStyleEditor(
              editors,
              "chartFirstDataLineStyle",
              symbol.chartFirstDataLineStyle,
              "chartSeries1"
            );
          }
        }
      }

      if (symbol.chartFirstDataField) {
        editors.push({
          key: "chartSecondDataField",
          label: "Data Field",
          kind: "select",
          value: symbol.chartSecondDataField,
          options: actionFieldOptions,
          allowClear: true,
          required: true,
          group: "chartSeries2",
          groupTitle: "Series 2"
        });

        if (symbol.chartSecondDataField) {
          pushChartColorEditors(
            editors,
            "Color",
            "chartSecondDataBgColor",
            "chartSecondDataBgColorSourceType",
            symbol.chartSecondDataBgColorSourceType,
            symbol.chartSecondDataBgColor,
            actionFieldOptions,
            "chartSeries2",
            isWebContext
          );
          if (isWebContext) {
            pushChartLegendEditor(
              editors,
              "Column Display Name",
              "chartSecondDataLegendLabel",
              symbol.chartSecondDataLegendLabel,
              "chartSeries2"
            );
          }
          if (showLineStyle) {
            pushChartLineStyleEditor(
              editors,
              "chartSecondDataLineStyle",
              symbol.chartSecondDataLineStyle,
              "chartSeries2"
            );
          }
        }
      }

      if (symbol.chartSecondDataField) {
        editors.push({
          key: "chartThirdDataField",
          label: "Data Field",
          kind: "select",
          value: symbol.chartThirdDataField,
          options: actionFieldOptions,
          allowClear: true,
          required: true,
          group: "chartSeries3",
          groupTitle: "Series 3"
        });

        if (symbol.chartThirdDataField) {
          pushChartColorEditors(
            editors,
            "Color",
            "chartThirdDataBgColor",
            "chartThirdDataBgColorSourceType",
            symbol.chartThirdDataBgColorSourceType,
            symbol.chartThirdDataBgColor,
            actionFieldOptions,
            "chartSeries3",
            isWebContext
          );
          if (isWebContext) {
            pushChartLegendEditor(
              editors,
              "Column Display Name",
              "chartThirdDataLegendLabel",
              symbol.chartThirdDataLegendLabel,
              "chartSeries3"
            );
          }
          if (showLineStyle) {
            pushChartLineStyleEditor(
              editors,
              "chartThirdDataLineStyle",
              symbol.chartThirdDataLineStyle,
              "chartSeries3"
            );
          }
        }
      }

      // KOA-18057: series four and beyond. Kept in a dynamic array so a chart can bind
      // an unbounded number of series. Mirrors the fixed-slot progressive reveal: the
      // next series field appears once the previous one is set, and clearing a field
      // removes that series and compacts the rest (handled in updateSymbolValue).
      // Web-only: the mobile generators read just the three named slots, so the extra
      // series never render on native mobile — hide the editors there (mobile chart
      // support comes later) rather than let users configure a no-op.
      if (isWebContext && symbol.chartThirdDataField) {
        const additionalSeries = symbol.chartAdditionalSeries ?? [];
        additionalSeries.forEach((extraSeries, extraIndex) => {
          const seriesNumber = extraIndex + 4;
          const group = `chartSeries${seriesNumber}`;
          editors.push({
            key: `chartAdditionalSeries.${extraIndex}.dataField`,
            label: "Data Field",
            kind: "select",
            value: extraSeries.dataField,
            options: actionFieldOptions,
            allowClear: true,
            required: true,
            group,
            groupTitle: `Series ${seriesNumber}`
          });
          if (extraSeries.dataField) {
            pushChartColorEditors(
              editors,
              "Color",
              `chartAdditionalSeries.${extraIndex}.bgColor`,
              `chartAdditionalSeries.${extraIndex}.bgColorSourceType`,
              extraSeries.bgColorSourceType,
              extraSeries.bgColor,
              actionFieldOptions,
              group,
              isWebContext
            );
            if (isWebContext) {
              pushChartLegendEditor(
                editors,
                "Column Display Name",
                `chartAdditionalSeries.${extraIndex}.legendLabel`,
                extraSeries.legendLabel,
                group
              );
            }
            if (showLineStyle) {
              pushChartLineStyleEditor(
                editors,
                `chartAdditionalSeries.${extraIndex}.lineStyle`,
                extraSeries.lineStyle,
                group
              );
            }
          }
        });

        // Trailing empty "Data Field" select that appends the next series when picked.
        // Only shown once every existing additional series already has a field set, so
        // there is never more than one empty slot open at a time.
        const lastSeriesReady =
          additionalSeries.length === 0 || Boolean(additionalSeries[additionalSeries.length - 1]?.dataField);
        if (lastSeriesReady) {
          const nextIndex = additionalSeries.length;
          const nextSeriesNumber = nextIndex + 4;
          editors.push({
            key: `chartAdditionalSeries.${nextIndex}.dataField`,
            label: "Data Field",
            kind: "select",
            value: undefined,
            options: actionFieldOptions,
            allowClear: true,
            group: `chartSeries${nextSeriesNumber}`,
            groupTitle: `Series ${nextSeriesNumber}`
          });
        }
      }
      break;

    case "CalendarOptions":
      editors.push(
        {
          key: "CalendarOptions.datavaluefield",
          label: "Event Id Field",
          kind: "select",
          value: symbol.CalendarOptions?.datavaluefield,
          options: actionFieldOptions
        },
        {
          key: "CalendarOptions.datatextfield",
          label: "Event Title",
          kind: "select",
          value: symbol.CalendarOptions?.datatextfield,
          options: actionFieldOptions
        },
        {
          key: "CalendarOptions.datastartfield",
          label: "Start Date",
          kind: "select",
          value: symbol.CalendarOptions?.datastartfield,
          options: actionFieldOptions
        },
        {
          key: "CalendarOptions.dataendfield",
          label: "End Date",
          kind: "select",
          value: symbol.CalendarOptions?.dataendfield,
          options: actionFieldOptions
        },
        {
          key: "CalendarOptions.datacolorfield",
          label: "Event Color Hex",
          kind: "select",
          value: symbol.CalendarOptions?.datacolorfield,
          options: actionFieldOptions,
          allowClear: true
        },
        {
          key: "CalendarOptions.dataheightfield",
          label: "Event Height",
          kind: "select",
          value: symbol.CalendarOptions?.dataheightfield,
          options: actionFieldOptions,
          allowClear: true
        },
        {
          key: "CalendarOptions.datafontcolorfield",
          label: "Font Color",
          kind: "select",
          value: symbol.CalendarOptions?.datafontcolorfield,
          options: actionFieldOptions,
          allowClear: true
        },
        {
          key: "CalendarOptions.datafontfamilyfield",
          label: "Font Family",
          kind: "select",
          value: symbol.CalendarOptions?.datafontfamilyfield,
          options: actionFieldOptions,
          allowClear: true
        },
        {
          key: "CalendarOptions.datafontsizefield",
          label: "Font Size",
          kind: "select",
          value: symbol.CalendarOptions?.datafontsizefield,
          options: actionFieldOptions,
          allowClear: true
        },
        {
          key: "CalendarOptions.datafontweightfield",
          label: "Font Weight",
          kind: "select",
          value: symbol.CalendarOptions?.datafontweightfield,
          options: actionFieldOptions,
          allowClear: true
        },
        {
          key: "CalendarOptions.dataiconfield",
          label: "Event Icon",
          kind: "select",
          value: symbol.CalendarOptions?.dataiconfield,
          options: actionFieldOptions,
          allowClear: true
        },
        {
          key: "CalendarOptions.dataavatarfield",
          label: "Event Avatars",
          kind: "select",
          value: symbol.CalendarOptions?.dataavatarfield,
          options: actionFieldOptions,
          allowClear: true
        }
      );

      // Progressive reveal (chart-style): the avatar cap only matters once an
      // avatar field is bound, so it appears only after dataavatarfield is set.
      if (symbol.CalendarOptions?.dataavatarfield) {
        editors.push({
          key: "CalendarOptions.avatarMaxCount",
          label: "Avatar Max Count",
          kind: "text",
          value: symbol.CalendarOptions?.avatarMaxCount
        });
      }
      break;

    case "MenuOptions":
      editors.push(
        {
          key: "MenuOptions.dataidfield",
          label: "Key Field",
          kind: "select",
          value: symbol.MenuOptions?.dataidfield,
          options: actionFieldOptions
        },
        {
          key: "MenuOptions.datatitlefield",
          label: "Title Field",
          kind: "select",
          value: symbol.MenuOptions?.datatitlefield,
          options: actionFieldOptions
        },
        {
          key: "MenuOptions.dataparentidfield",
          label: "Parent Key Field",
          kind: "select",
          value: symbol.MenuOptions?.dataparentidfield,
          options: actionFieldOptions
        },
        {
          key: "MenuOptions.dataorderfield",
          label: "Order Field",
          kind: "select",
          value: symbol.MenuOptions?.dataorderfield,
          options: actionFieldOptions
        },
        {
          key: "MenuOptions.datalinkfield",
          label: "Link Field",
          kind: "select",
          value: symbol.MenuOptions?.datalinkfield,
          options: actionFieldOptions
        },
        {
          key: "MenuOptions.dataiconfield",
          label: "Icon Field",
          kind: "select",
          value: symbol.MenuOptions?.dataiconfield,
          options: actionFieldOptions
        },
        {
          key: "MenuOptions.databackgroundcolorfield",
          label: "Background Color Field",
          kind: "select",
          value: symbol.MenuOptions?.databackgroundcolorfield,
          options: actionFieldOptions
        }
      );
      break;

    case "BreadcrumbOptions":
      editors.push(
        {
          key: "BreadcrumbOptions.dataidfield",
          label: "Key Field",
          kind: "select",
          value: symbol.BreadcrumbOptions?.dataidfield,
          options: actionFieldOptions
        },
        {
          key: "BreadcrumbOptions.datatitlefield",
          label: "Title Field",
          kind: "select",
          value: symbol.BreadcrumbOptions?.datatitlefield,
          options: actionFieldOptions
        },
        {
          key: "BreadcrumbOptions.dataparentidfield",
          label: "Parent Key Field",
          kind: "select",
          value: symbol.BreadcrumbOptions?.dataparentidfield,
          options: actionFieldOptions
        },
        {
          key: "BreadcrumbOptions.dataorderfield",
          label: "Order Field",
          kind: "select",
          value: symbol.BreadcrumbOptions?.dataorderfield,
          options: actionFieldOptions
        },
        {
          key: "BreadcrumbOptions.datalinkfield",
          label: "Link Field",
          kind: "select",
          value: symbol.BreadcrumbOptions?.datalinkfield,
          options: actionFieldOptions
        },
        {
          key: "BreadcrumbOptions.dataiconfield",
          label: "Icon Field",
          kind: "select",
          value: symbol.BreadcrumbOptions?.dataiconfield,
          options: actionFieldOptions
        }
      );
      break;

    case "RadialChartOptions":
      editors.push(
        {
          key: "RadialChartOptions.dataslicelabelfield",
          label: "Slice Label Field",
          kind: "select",
          value: symbol.RadialChartOptions?.dataslicelabelfield,
          options: actionFieldOptions
        },
        {
          key: "RadialChartOptions.dataslicetextfield",
          label: "Slice Text Field",
          kind: "select",
          value: symbol.RadialChartOptions?.dataslicetextfield,
          options: actionFieldOptions
        },
        {
          key: "RadialChartOptions.dataslicepercentagefield",
          label: "Percentage Field",
          kind: "select",
          value: symbol.RadialChartOptions?.dataslicepercentagefield,
          options: actionFieldOptions,
          required: true
        },
        {
          key: "RadialChartOptions.dataslicepointfield",
          label: "Point Field",
          kind: "select",
          value: symbol.RadialChartOptions?.dataslicepointfield,
          options: actionFieldOptions,
          required: true
        }
      );
      break;

    case "RadialChartLevelOptions":
      editors.push(
        {
          key: "RadialChartLevelOptions.dataleveltextfield",
          label: "Level Text Field",
          kind: "select",
          value: symbol.RadialChartLevelOptions?.dataleveltextfield,
          options: actionFieldOptions
        },
        {
          key: "RadialChartLevelOptions.datalevelpercentagefield",
          label: "Level Percentage Field",
          kind: "select",
          value: symbol.RadialChartLevelOptions?.datalevelpercentagefield,
          options: actionFieldOptions
        },
        {
          key: "RadialChartLevelOptions.datalevelcolorfield",
          label: "Level Color Field",
          kind: "select",
          value: symbol.RadialChartLevelOptions?.datalevelcolorfield,
          options: actionFieldOptions
        }
      );
      break;

    case "MixedChartOptions":
      editors.push({
        key: "MixedChartOptions.dataxaxisesfield",
        label: "X Axis Field",
        kind: "select",
        value: symbol.MixedChartOptions?.dataxaxisesfield,
        options: actionFieldOptions,
        required: true
      });
      break;

    case "MixedChartLineOptions":
      editors.push(
        {
          key: "MixedChartLineOptions.dataxaxisfield",
          label: "X Axis Field",
          kind: "select",
          value: symbol.MixedChartLineOptions?.dataxaxisfield,
          options: actionFieldOptions,
          required: true
        },
        {
          key: "MixedChartLineOptions.dataxaxisvaluefield",
          label: "Axis Value Field",
          kind: "select",
          value: symbol.MixedChartLineOptions?.dataxaxisvaluefield,
          options: actionFieldOptions,
          required: true
        },
        {
          key: "MixedChartLineOptions.dataxaxistooltipfield",
          label: "Custom Tooltip Title",
          kind: "select",
          value: symbol.MixedChartLineOptions?.dataxaxistooltipfield,
          options: actionFieldOptions
        },
        {
          key: "MixedChartLineOptions.datavaluetooltipfield",
          label: "Custom Tooltip Value",
          kind: "select",
          value: symbol.MixedChartLineOptions?.datavaluetooltipfield,
          options: actionFieldOptions
        }
      );
      break;

    case "MixedChartPointOptions":
      editors.push(
        {
          key: "MixedChartPointOptions.dataxaxisfield",
          label: "X Axis Field",
          kind: "select",
          value: symbol.MixedChartPointOptions?.dataxaxisfield,
          options: actionFieldOptions,
          required: true
        },
        {
          key: "MixedChartPointOptions.datavaluefield",
          label: "Axis Value Field",
          kind: "select",
          value: symbol.MixedChartPointOptions?.datavaluefield,
          options: actionFieldOptions,
          required: true
        },
        {
          key: "MixedChartPointOptions.dataxaxistooltipfield",
          label: "Custom Tooltip Title",
          kind: "select",
          value: symbol.MixedChartPointOptions?.dataxaxistooltipfield,
          options: actionFieldOptions
        },
        {
          key: "MixedChartPointOptions.datavaluetooltipfield",
          label: "Custom Tooltip Value",
          kind: "select",
          value: symbol.MixedChartPointOptions?.datavaluetooltipfield,
          options: actionFieldOptions
        }
      );
      break;

    case "MixedChartBarOptions":
      editors.push(
        {
          key: "MixedChartBarOptions.dataxaxisfield",
          label: "X Axis Field",
          kind: "select",
          value: symbol.MixedChartBarOptions?.dataxaxisfield,
          options: actionFieldOptions,
          required: true
        },
        {
          key: "MixedChartBarOptions.datavaluefield",
          label: "Axis Value Field",
          kind: "select",
          value: symbol.MixedChartBarOptions?.datavaluefield,
          options: actionFieldOptions,
          required: true
        },
        {
          key: "MixedChartBarOptions.dataxaxistooltipfield",
          label: "Custom Tooltip Title",
          kind: "select",
          value: symbol.MixedChartBarOptions?.dataxaxistooltipfield,
          options: actionFieldOptions
        },
        {
          key: "MixedChartBarOptions.datavaluetooltipfield",
          label: "Custom Tooltip Value",
          kind: "select",
          value: symbol.MixedChartBarOptions?.datavaluetooltipfield,
          options: actionFieldOptions
        }
      );
      break;

    case "TimelineOptions":
      editors.push(
        {
          key: "TimelineOptions.datatitlefield",
          label: "Title Field",
          kind: "select",
          value: symbol.TimelineOptions?.datatitlefield,
          options: actionFieldOptions
        },
        {
          key: "TimelineOptions.datadescriptionfield",
          label: "Description Field",
          kind: "select",
          value: symbol.TimelineOptions?.datadescriptionfield,
          options: actionFieldOptions
        },
        {
          key: "TimelineOptions.datacolorfield",
          label: "Color field",
          kind: "select",
          value: symbol.TimelineOptions?.datacolorfield,
          options: actionFieldOptions
        }
      );
      break;

    case "ChatOptions":
      editors.push(
        {
          key: "ChatOptions.chatId",
          label: "Message Id",
          kind: "select",
          value: symbol.ChatOptions?.chatId,
          options: actionFieldOptions
        },
        {
          key: "ChatOptions.chatMessage",
          label: "Message",
          kind: "select",
          value: symbol.ChatOptions?.chatMessage,
          options: actionFieldOptions
        },
        {
          key: "ChatOptions.chatUserDisplayName",
          label: "User Display Name",
          kind: "select",
          value: symbol.ChatOptions?.chatUserDisplayName,
          options: actionFieldOptions
        },
        {
          key: "ChatOptions.chatUserName",
          label: "Username",
          kind: "select",
          value: symbol.ChatOptions?.chatUserName,
          options: actionFieldOptions
        },
        {
          key: "ChatOptions.chatUserAvatar",
          label: "User Avatar",
          kind: "select",
          value: symbol.ChatOptions?.chatUserAvatar,
          options: actionFieldOptions
        },
        {
          key: "ChatOptions.chatDate",
          label: "Date",
          kind: "select",
          value: symbol.ChatOptions?.chatDate,
          options: actionFieldOptions
        }
      );
      break;
  }

  return editors.filter((editor) => editor.visible !== false);
}

function clearActionValuesForToolType(symbol: ISymbol, propertyToolType: string): void {
  switch (propertyToolType) {
    case "TreeOptions":
      symbol.fieldToDisplay = undefined;
      symbol.fieldToUseAsKey = undefined;
      symbol.fieldToUseAsParent = undefined;
      symbol.fieldToUseAsChecked = undefined;
      symbol.fieldToUseAsIcon = undefined;
      symbol.fieldToUseAsColor = undefined;
      symbol.fieldsToUseAsColumns = undefined;
      break;

    case "DirectoryOptions":
      symbol.fieldToDisplay = undefined;
      symbol.fieldToUseAsKey = undefined;
      symbol.fieldToUseAsParent = undefined;
      break;

    case "CalendarOptions":
      symbol.CalendarOptions = {};
      break;

    case "MenuOptions":
      symbol.MenuOptions = {};
      break;

    case "BreadcrumbOptions":
      symbol.BreadcrumbOptions = {};
      break;

    case "RadialChartOptions":
      symbol.RadialChartOptions = {};
      break;

    case "RadialChartLevelOptions":
      symbol.RadialChartLevelOptions = {};
      break;

    case "MixedChartOptions":
      symbol.MixedChartOptions = {};
      break;

    case "MixedChartLineOptions":
      symbol.MixedChartLineOptions = {};
      break;

    case "MixedChartPointOptions":
      symbol.MixedChartPointOptions = {};
      break;

    case "MixedChartBarOptions":
      symbol.MixedChartBarOptions = {};
      break;

    case "TimelineOptions":
      symbol.TimelineOptions = {};
      break;

    case "KanbanBoardCardOptions":
      symbol.KanbanBoardCardOptions = {};
      break;

    case "KanbanBoardLaneOptions":
      symbol.KanbanBoardLaneOptions = {};
      break;

    case "NodeTreeNodeOptions":
      symbol.NodeTreeNodeOptions = {};
      break;

    case "Options":
      symbol.fieldToDisplay = undefined;
      symbol.fieldToUseAsKey = undefined;
      symbol.fieldToUseAsParent = undefined;
      symbol.fieldToUseAsChecked = undefined;
      // KOA-18299 — these name columns of the bound action's result, so they are as stale as the
      // rest once the action changes; leaving them behind would emit a datagroupfield pointing at
      // a column the new datasource does not have.
      symbol.fieldToUseAsGroup = undefined;
      symbol.fieldToUseAsTagColor = undefined;
      symbol.fixedValue = undefined;
      symbol.isFixed = false;
      break;
  }

  symbol.actionDefinitionId = undefined;
  symbol.actionDefinitionObjectPath = undefined;
  symbol.resultActionId = undefined;
}

/**
 * KOA-18179 — pick the option-list KEY / DISPLAY columns out of an action's
 * return schema.
 *
 * An `options` symbol carries `fieldToUseAsKey` + `fieldToDisplay`, and the two
 * legal shapes name COMPLETELY DIFFERENT things: a FIXED list's items are
 * literally `{key, text}` (hence `fieldToUseAsKey="key"` / `fieldToDisplay="text"`
 * at the `case "fixedValue"` branch), while an ACTION-fed list names columns of
 * that action's result. Binding an action while the fixed-shape names survive
 * produces a symbol that points at fields the action does not have — the live
 * defect this exists to prevent:
 *
 *   Target field 'text' of action 'GetCategories' no longer exists.
 *   Target field 'key' of action 'GetCategories' no longer exists. (Property: 'options'.)
 *
 * Clearing alone is not enough: the generated `<KSelectBox>` renders an EMPTY
 * dropdown when it has no display column (see the `fieldToDisplay` note in
 * `setDesignerPropertyDataField`), so we would trade a blocking validation error
 * for a silently empty control. Deriving keeps the dropdown working when the
 * caller did not name the columns itself.
 *
 * Deliberately conservative — a caller that DOES name the columns
 * (`ui_property.set_datasource_bindings` with `dataTextField`/`dataValueField`,
 * or the property panel) overwrites these afterwards, so this only fills a gap.
 * Returns `{}` when the schema is unknown; never guesses a name that is not in
 * the schema.
 */
const OPTION_DISPLAY_NAME_PREFERENCE: readonly string[] = [
  "name",
  "title",
  "label",
  "text",
  "displayname",
  "description",
  "code"
];

/** Audit / plumbing columns that must never become the visible option label. */
const OPTION_DISPLAY_EXCLUDED: ReadonlySet<string> = new Set<string>([
  "id",
  "isdeleted",
  "createdat",
  "updatedat",
  "createduser_id",
  "updateduser_id",
  "parentid",
  "kuser_id"
]);

export function deriveOptionFieldsFromReturnType(returnType: IKType | undefined): {
  fieldToUseAsKey?: string;
  fieldToDisplay?: string;
} {
  const elements: IKTypeField[] = returnType?.elements ?? [];
  const scalar: IKTypeField[] = elements.filter((e: IKTypeField): boolean => e?.isList !== true && !!e?.name);
  if (scalar.length === 0) return {};

  const byLower = (name: string): IKTypeField | undefined =>
    scalar.find((e: IKTypeField): boolean => e.name.toLowerCase() === name);

  // KEY: the primary key by convention, else the first Guid, else the first column.
  const key: IKTypeField | undefined =
    byLower("id") ??
    scalar.find((e: IKTypeField): boolean => (e.type?.name ?? "").toLowerCase() === "guid") ??
    scalar[0];

  // DISPLAY: a human-readable label that is NOT the key and NOT an audit column.
  const displayCandidates: IKTypeField[] = scalar.filter(
    (e: IKTypeField): boolean => e.name !== key?.name && !OPTION_DISPLAY_EXCLUDED.has(e.name.toLowerCase())
  );
  const preferred: IKTypeField | undefined = OPTION_DISPLAY_NAME_PREFERENCE.map(
    (wanted: string): IKTypeField | undefined =>
      displayCandidates.find((e: IKTypeField): boolean => e.name.toLowerCase() === wanted)
  ).find((hit: IKTypeField | undefined): boolean => hit !== undefined);
  const display: IKTypeField | undefined =
    preferred ??
    displayCandidates.find((e: IKTypeField): boolean => (e.type?.name ?? "").toLowerCase() === "string") ??
    displayCandidates[0];

  return {
    ...(key?.name ? { fieldToUseAsKey: key.name } : {}),
    ...(display?.name ? { fieldToDisplay: display.name } : {})
  };
}

/**
 * KOA-18560 — every symbol field that names a column of the bound action's result. It is the
 * ONE list both ways of leaving an action run through: swapping to another action and clearing
 * it with the selector's X. The two used to keep separate lists that had drifted in both
 * directions: the clear kept the hidden key/display columns, so the next action inherited them
 * and validation reported KV1008 for a column it never had; the swap kept the timeline and the
 * option group / tag-colour columns, so they silently pointed at the old table.
 */
function clearDependentActionFields(symbol: ISymbol): void {
  symbol.fieldToUseAsKey = undefined;
  symbol.fieldToUseAsParent = undefined;
  symbol.fieldToUseAsChecked = undefined;
  symbol.fieldToUseAsIcon = undefined;
  symbol.fieldToUseAsColor = undefined;
  symbol.fieldToUseAsGroup = undefined;
  symbol.fieldToUseAsTagColor = undefined;
  symbol.fieldsToUseAsColumns = undefined;
  symbol.fieldToDisplay = undefined;
  symbol.TimelineOptions = {};
  symbol.CalendarOptions = {};
  symbol.ChatOptions = {};
  symbol.MenuOptions = {};
  symbol.BreadcrumbOptions = {};
  symbol.RadialChartOptions = {};
  symbol.RadialChartLevelOptions = {};
  symbol.MixedChartOptions = {};
  symbol.MixedChartLineOptions = {};
  symbol.MixedChartPointOptions = {};
  symbol.MixedChartBarOptions = {};
  symbol.KanbanBoardCardOptions = {};
  symbol.KanbanBoardLaneOptions = {};
  symbol.NodeTreeNodeOptions = {};
  symbol.bagName = undefined;
  symbol.chartDataLabel = undefined;
  symbol.chartTooltipFooterDataLabel = undefined;
  symbol.chartFirstDataField = undefined;
  symbol.chartSecondDataField = undefined;
  symbol.chartThirdDataField = undefined;
  symbol.chartDataDisplayLabel = undefined;
  symbol.chartMultiTooltipContentField = undefined;
  symbol.chartTargetLineField = undefined;
  symbol.chartFirstDataBgColor = undefined;
  symbol.chartSecondDataBgColor = undefined;
  symbol.chartThirdDataBgColor = undefined;
  symbol.chartFirstDataLineStyle = undefined;
  symbol.chartSecondDataLineStyle = undefined;
  symbol.chartThirdDataLineStyle = undefined;
  symbol.chartAdditionalSeries = undefined;
  symbol.valuePlaceholderText = undefined;
  symbol.fieldToLongitude = undefined;
  symbol.fieldToLatitude = undefined;
}

function resolveActionSelection(
  sd: IScreenDesignContextState,
  value: string
): {
  action?: IAction;
  actionDef?: IActionDefinitionDto;
  actionName: string;
} {
  const [actionName, actionTag] = value.split("#");

  // Try name-based lookup first (original flow)
  let action = sd
    ?.getDefinedActions?.()
    .find((candidate: IAction) => candidate.label === actionName && candidate.tag === actionTag) as IAction | undefined;

  if (!action && actionName.includes(".")) {
    const baseName = actionName.split(".")[0];
    action = sd?.getDefinedActions?.().find((candidate: IAction) => candidate.label === baseName);
  }

  // An action already on the screen names its own definition. A name is not enough: two
  // datasources may each hold a "GetUsers", and a name lookup paired "GetUsers#erp" with whichever
  // definition was listed first (KOA-18147).
  const instanceDefinitionId: string | undefined = action?.actionDefinitionId;
  let actionDef: IActionDefinitionDto | undefined = instanceDefinitionId
    ? sd?.actionDefinitions?.find((candidate: IActionDefinitionDto): boolean => candidate.id === instanceDefinitionId)
    : undefined;

  if (!actionDef) {
    actionDef = sd?.actionDefinitions?.find(
      (candidate: IActionDefinitionDto): boolean => candidate.name === actionName.split(".")[0]
    );
  }

  // If not found by name, try ID-based lookup (MCP tools pass action definition GUIDs)
  if (!actionDef) {
    actionDef = sd?.actionDefinitions?.find((candidate: IActionDefinitionDto) => candidate.id === actionName) as
      | IActionDefinitionDto
      | undefined;
  }

  // Last resort: the same lenient name / displayName match the attach path uses
  // (`ui_logic_add_action_to_event` — case- and whitespace-insensitive). The two
  // must agree, or a name the bootstrap can attach would be "unknown" here.
  if (!actionDef) {
    const normalize = (text: string | undefined): string => (text || "").toLowerCase().replace(/\s+/g, "");
    const wantedName: string = normalize(actionName.split(".")[0]);
    actionDef = sd?.actionDefinitions?.find(
      (candidate: IActionDefinitionDto): boolean =>
        wantedName !== "" &&
        (normalize(candidate.name) === wantedName || normalize(candidate.displayName) === wantedName)
    );
  }

  if (!action && actionDef) {
    // Find the action instance on the screen that uses this definition
    action = sd?.getDefinedActions?.().find((candidate: IAction) => candidate.actionDefinitionId === actionDef!.id) as
      | IAction
      | undefined;
  }

  // Use the definition name as actionName when we resolved by ID,
  // but preserve the dotted field suffix (e.g. "actionx.employee_list") so the
  // Table options picker can descend into the list's item type.
  const dotIndex = actionName.indexOf(".");
  const fieldSuffix = dotIndex >= 0 ? actionName.substring(dotIndex) : "";
  const resolvedBase = actionDef?.name || (dotIndex >= 0 ? actionName.substring(0, dotIndex) : actionName);
  const resolvedName = resolvedBase + fieldSuffix;

  return { action, actionDef, actionName: resolvedName };
}

/** Upper bound on the names an "unknown action" error lists — keeps the error readable. */
const MAX_LISTED_DATA_ACTION_NAMES: number = 40;

/**
 * Names a caller may legitimately pass as a data action: every action already
 * on the screen, plus every list-returning definition (bindable before it is
 * attached — `set_datasource_bindings` bootstraps the instance).
 */
function getBindableDataActionNames(sd: IScreenDesignContextState): string[] {
  const names: Set<string> = new Set<string>();
  const definedActions: IAction[] = sd?.getDefinedActions?.() || [];
  definedActions.forEach((action: IAction): void => {
    if (action.label) names.add(action.label);
  });
  const definitions: IActionDefinitionDto[] = sd?.actionDefinitions || [];
  definitions.forEach((definition: IActionDefinitionDto): void => {
    if (definition.name && definition.returnCardinality === ECardinality.Many) names.add(definition.name);
  });
  return Array.from(names).sort();
}

function buildUnknownDataActionError(sd: IScreenDesignContextState, requested: string): string {
  const names: string[] = getBindableDataActionNames(sd);
  const listed: string[] = names.slice(0, MAX_LISTED_DATA_ACTION_NAMES);
  const omitted: number = names.length - listed.length;
  let available: string = "No list-returning action exists in this project yet — create the action first.";
  if (listed.length > 0) {
    const more: string = omitted > 0 ? ` (+${omitted} more)` : "";
    available = `Available actions: ${listed.join(", ")}${more}.`;
  }
  return `Unknown action "${requested}" — no action on this screen and no action definition has that name or id, so nothing was bound. ${available}`;
}

/**
 * The Table `rowKey` a freshly bound dataSource should use, or `undefined` to
 * leave it alone. Only the column that IS the key qualifies — matched
 * case-insensitively so the schema's real casing (`Id`, `ID`) replaces the
 * component default `id`, which matches no column and leaves rows keyless.
 * Falls back to the first Guid column; never guesses a non-key column, because
 * a non-unique rowKey is worse than a missing one.
 */
function deriveRowKeyFromFields(fields: IKTypeField[], currentRowKey: string): string | undefined {
  const scalar: IKTypeField[] = fields.filter((field: IKTypeField): boolean => field?.isList !== true && !!field?.name);
  if (scalar.some((field: IKTypeField): boolean => field.name === currentRowKey)) {
    return undefined;
  }
  const wanted: string = (currentRowKey || "id").toLowerCase();
  const key: IKTypeField | undefined =
    scalar.find((field: IKTypeField): boolean => field.name.toLowerCase() === wanted) ??
    scalar.find((field: IKTypeField): boolean => field.name.toLowerCase() === "id") ??
    scalar.find((field: IKTypeField): boolean => (field.type?.name ?? "").toLowerCase() === "guid");
  return key?.name;
}

/**
 * After a dataSource action is bound: if the component has a `rowKey` that is
 * still the untouched component-set default, point it at the action's real key
 * column. A rowKey the user (or an earlier call) changed is never overwritten.
 * Returns the rowKey written, or `undefined` when nothing changed.
 */
function syncDefaultRowKeyToDataSource(context: PropertyContext, dataSourceSymbol: ISymbol): string | undefined {
  if (context.property.name !== "dataSource") return undefined;
  const rowKeyProperty: IDisplayItemProperty | undefined = context.di.item.properties?.find(
    (candidate: IDisplayItemProperty): boolean => candidate.name === "rowKey"
  );
  if (!rowKeyProperty) return undefined;

  const currentRowKey: unknown = rowKeyProperty.symbol?.fixedValue;
  const currentText: string = typeof currentRowKey === "string" ? currentRowKey : "";
  const components: IComponent[] = context.sd?.project?.componentSetObj?.components || [];
  const defaultRowKey: unknown = components
    .find((component: IComponent): boolean => component.name === context.di.item.name)
    ?.properties?.find((candidate: IProperty): boolean => candidate.name === "rowKey")?.symbol?.fixedValue;
  const isUntouchedDefault: boolean = currentText === "" || currentText === defaultRowKey;
  if (!isUntouchedDefault) return undefined;

  const fields: IKTypeField[] = context.propertySdHelper.getActionFieldsBySymbol(dataSourceSymbol);
  const derivedRowKey: string | undefined = deriveRowKeyFromFields(fields, currentText);
  if (derivedRowKey === undefined) return undefined;

  const rowKeySymbol: ISymbol = _.cloneDeep(rowKeyProperty.symbol || {}) as ISymbol;
  applyDefaultFieldValue(rowKeySymbol, derivedRowKey);
  const writeResult: ReturnType<typeof setDesignerPropertySymbol> = setDesignerPropertySymbol(
    context.sdInstance,
    { kuikaId: context.di.id!.toString(), propertyName: "rowKey", symbol: rowKeySymbol },
    ESdChangeReason.ChangedCustomFieldOnPropertyPanel
  );
  return "error" in writeResult ? undefined : derivedRowKey;
}

function setNestedOptionValue(symbol: ISymbol, key: string, value: unknown): void {
  const [groupName, fieldName] = key.split(".");
  if (!groupName || !fieldName) {
    return;
  }

  const target = (_.cloneDeep((symbol as unknown as Record<string, unknown>)[groupName]) || {}) as Record<
    string,
    unknown
  >;
  if (value === undefined || value === "") {
    delete target[fieldName];
  } else {
    target[fieldName] = value;
  }
  (symbol as unknown as Record<string, unknown>)[groupName] = target;
}

/**
 * KOA-18057: applies one edit to a series in `symbol.chartAdditionalSeries` (series
 * four and beyond). Keys are `chartAdditionalSeries.{index}.{field}`.
 *
 * - Setting `dataField` on the trailing (index === length) slot appends a new series.
 * - Clearing a series' `dataField` removes that series and compacts the rest, so the
 *   list never has gaps (mirrors the fixed-slot promote-on-clear behaviour).
 * - Other fields (bgColor / bgColorSourceType / legendLabel / lineStyle) update in
 *   place; "solid" line style clears back to the default. Out-of-range edits are
 *   ignored so a stale key can never create a fieldless series.
 */
function applyChartAdditionalSeriesField(symbol: ISymbol, key: string, value: unknown): void {
  const parts: string[] = key.split(".");
  if (parts.length !== 3) {
    return;
  }
  const index: number = Number(parts[1]);
  const field: string = parts[2];
  if (!Number.isInteger(index) || index < 0) {
    return;
  }

  const list: ChartAdditionalSeries[] = Array.isArray(symbol.chartAdditionalSeries)
    ? [...symbol.chartAdditionalSeries]
    : [];
  const stringValue: string | undefined = typeof value === "string" ? value : undefined;
  const isEmpty: boolean = value === undefined || value === "" || (field === "lineStyle" && value === "solid");

  if (field === "dataField") {
    if (isEmpty) {
      if (index < list.length) {
        list.splice(index, 1);
      }
    } else if (index < list.length) {
      list[index] = { ...list[index], dataField: stringValue };
    } else {
      list.push({ dataField: stringValue });
    }
  } else if (index < list.length) {
    const entry: ChartAdditionalSeries = { ...list[index] };
    if (isEmpty) {
      delete (entry as unknown as Record<string, unknown>)[field];
    } else {
      (entry as unknown as Record<string, unknown>)[field] = stringValue;
    }
    list[index] = entry;
  }

  symbol.chartAdditionalSeries = list.length > 0 ? list : undefined;
}

function setCategoryForField(symbol: ISymbol, key: string): void {
  if (key === "fieldToUseAsParent") {
    symbol.category = ESymbolCategory.actionResult;
    return;
  }

  if (
    key.startsWith("RadialChartOptions.") ||
    key.startsWith("RadialChartLevelOptions.") ||
    key.startsWith("MixedChartOptions.") ||
    key.startsWith("MixedChartLineOptions.") ||
    key.startsWith("MixedChartPointOptions.") ||
    key.startsWith("MixedChartBarOptions.") ||
    key.startsWith("TimelineOptions.") ||
    key.startsWith("KanbanBoardLaneOptions.") ||
    key.startsWith("KanbanBoardCardOptions.")
  ) {
    symbol.category = ESymbolCategory.fixed;
  }
}

function applyDefaultFieldValue(symbol: ISymbol, value: unknown): void {
  symbol.fixedValue = value;
  symbol.description = "";
  symbol.fieldName = "";
  symbol.fieldToDisplay = "";
  symbol.category = ESymbolCategory.fixed;
}

/** What a table column's edit-mode swap may do to the column's children. */
interface IColumnEditModeSwap {
  /** The edit-mode component the swap replaces; undefined when the column has none yet. */
  editModeComponent?: IDisplayItem;
  /** The component to keep selected through the swap's delete/add; undefined lets it follow the new one. */
  kuikaIdToFocus?: string;
}

/**
 * Decides whether the edit-mode swap may restructure a table column, mirroring the generated table: a
 * column switches to edit mode only with exactly two children and shows the second one while a row is
 * edited. A Label is never that edit-mode component — the column's own child list offers only Label,
 * and the swap is the only thing that adds an editor — so a column holding two Labels belongs to the
 * user and is left alone, as is any other shape. Undefined means: write the binding, touch no children.
 *
 * The swap's delete/add select what they touch unless told otherwise, so it keeps the user's
 * selection — except when the selected component is the one being replaced, where the selection
 * follows its replacement instead of pointing at a deleted component (KOA-17203).
 */
function getColumnEditModeSwap(
  column: IDisplayItem,
  selectedKuikaId: string | undefined
): IColumnEditModeSwap | undefined {
  const children: IDisplayItem[] = column.children || [];
  let editModeComponent: IDisplayItem | undefined;
  if (children.length === 2 && children[1]?.item?.name !== "Label") {
    editModeComponent = children[1];
  } else if (children.length !== 1) {
    return undefined;
  }

  const isReplacingSelection: boolean =
    editModeComponent !== undefined && selectedKuikaId === editModeComponent.id?.toString();
  return { editModeComponent, kuikaIdToFocus: isReplacingSelection ? undefined : selectedKuikaId };
}

async function applyTableFieldBinding(
  context: PropertyContext,
  rawValue: string | undefined,
  prePath?: string
): Promise<{ success: true; message: string } | { error: string }> {
  const symbol = _.cloneDeep(context.property.symbol || {});
  symbol.fieldToDisplay = rawValue;

  if (rawValue) {
    let selectedField: any;
    if (typeof rawValue === "string") {
      try {
        selectedField = JSON.parse(rawValue);
      } catch {
        // MCP caller passed a plain field name — resolve from table fields
        const tableFields = getTableFieldOptions(context);
        const match = tableFields.find((f) => f.name === rawValue || f.name.toLowerCase() === rawValue.toLowerCase());
        selectedField = match || { name: rawValue, type: "String", isList: false };
      }
    } else {
      selectedField = rawValue;
    }
    if (selectedField?.prefix) {
      prePath = selectedField.prefix;
    }

    buildTableFieldBindingSymbolFields(symbol, selectedField as IKTypeField, prePath);
  } else {
    const componentDefinition = context.sd?.getComponentDefinition?.(context.di.item) as
      | IComponentDefinition
      | undefined;
    if (componentDefinition?.componentCategoryName === "Data") {
      symbol.category = ESymbolCategory.fixed;
    } else {
      symbol.fixedValue = context.di?.item?.name;
      symbol.description = "";
      symbol.fieldName = "";
      symbol.fieldToDisplay = "";
      symbol.category = ESymbolCategory.fixed;
    }
  }

  const dict = getScreenDictionary(context.sd);
  const targetId = context.di.id;
  const targetDi = dict[targetId!] as IDisplayItem | undefined;
  const parentDi = targetDi?.parentId ? dict[targetDi.parentId] : undefined;
  const isColumnChild: boolean =
    parentDi?.item?.name === "EditableTableColumn" || parentDi?.item?.name === "TableDefaultColumn";
  // Handing the selection back in a later commit is not enough — the right panel does not follow that
  // commit's reason — so the swap's own delete/add carry it (KOA-17203).
  const swap: IColumnEditModeSwap | undefined =
    parentDi && isColumnChild && targetDi?.item?.name !== "SelectBox"
      ? getColumnEditModeSwap(parentDi, context.sd?.selectedKuikaIds?.[0])
      : undefined;

  if (parentDi && swap) {
    const rendererNames = CommonHelperFunctions.getTableColumnEditModeRenderer(symbol.fieldType);

    if (swap.editModeComponent) {
      await context.sd?.deleteItemByKuikaId?.(swap.editModeComponent.id?.toString(), swap.kuikaIdToFocus);
    }
    if (rendererNames.length > 0) {
      await context.sd?.addChildItemByComponentName?.(
        parentDi.id!.toString(),
        rendererNames[0],
        undefined,
        swap.kuikaIdToFocus
      );
    }

    const latestParent = getScreenDictionary(context.sd)[parentDi.id!] as IDisplayItem | undefined;
    const latestRendererChild =
      latestParent?.children && latestParent.children.length > 1 ? latestParent.children[1] : undefined;

    if (latestRendererChild) {
      const rendererParams = {
        propertyName: context.property.name,
        symbol,
        kuikaId: latestRendererChild.id
      } as unknown as IApplyPropertyGridChangesToDisplayParameters;
      applyPropertyGridChange(context.sd, rendererParams, ESdChangeReason.ChangedCustomFieldOnPropertyPanel);
    }

    if (parentDi.item.name === "TableDefaultColumn" && latestParent?.children?.[0]) {
      const firstChildParams = {
        propertyName: context.property.name,
        symbol,
        kuikaId: latestParent.children[0].id
      } as unknown as IApplyPropertyGridChangesToDisplayParameters;
      applyPropertyGridChange(context.sd, firstChildParams, ESdChangeReason.ChangedCustomFieldOnPropertyPanel);
    }
  }

  return setDesignerPropertySymbol(
    context.sdInstance,
    {
      kuikaId: context.di.id!.toString(),
      propertyName: context.property.name,
      symbol,
      screenId: context.screenId
    },
    ESdChangeReason.ChangedCustomFieldOnPropertyPanel
  );
}

async function applyTableEditModeRenderer(
  context: PropertyContext,
  value: string | undefined,
  id?: string | number
): Promise<{ success: true; message: string } | { error: string }> {
  const symbol = _.cloneDeep(context.property.symbol || {});

  if (value === undefined) {
    symbol.fixedValue = "";
    symbol.description = "";
    symbol.fieldName = "";
    symbol.fieldToDisplay = "";
    symbol.category = ESymbolCategory.fixed;
    return setDesignerPropertySymbol(
      context.sdInstance,
      {
        kuikaId: context.di.id!.toString(),
        propertyName: context.property.name,
        symbol,
        screenId: context.screenId
      },
      ESdChangeReason.ChangedCustomFieldOnPropertyPanel
    );
  }

  symbol.tableEditModeRenderer = value;
  const dict = getScreenDictionary(context.sd);
  const targetId = id ? parseInt(id.toString(), 10) : context.di.id!;
  const targetDi = dict[targetId] as IDisplayItem | undefined;
  const parentDi = targetDi?.parentId ? dict[targetDi.parentId] : undefined;
  const secondChildId = parentDi?.children && parentDi.children.length > 1 ? parentDi.children[1]?.id : undefined;
  // Same swap as `applyTableFieldBinding`, so the same rule; unlike it, this one never adds an
  // edit-mode component to a column that has none.
  const swap: IColumnEditModeSwap | undefined = parentDi
    ? getColumnEditModeSwap(parentDi, context.sd?.selectedKuikaIds?.[0])
    : undefined;

  if (!parentDi || !swap?.editModeComponent) {
    return setDesignerPropertySymbol(
      context.sdInstance,
      {
        kuikaId: context.di.id!.toString(),
        propertyName: context.property.name,
        symbol,
        screenId: context.screenId
      },
      ESdChangeReason.ChangedCustomFieldOnPropertyPanel
    );
  }

  await context.sd?.deleteItemByKuikaId?.(swap.editModeComponent.id?.toString(), swap.kuikaIdToFocus);
  await context.sd?.addChildItemByComponentName?.(parentDi.id!.toString(), value || "", undefined, swap.kuikaIdToFocus);

  const latestParent = getScreenDictionary(context.sd)[parentDi.id!] as IDisplayItem | undefined;
  const latestRendererChild =
    latestParent?.children && latestParent.children.length > 1 ? latestParent.children[1] : undefined;

  if (latestRendererChild) {
    const rendererParams = {
      propertyName: latestRendererChild.item.name === "Checkbox" ? "checked" : context.property.name,
      symbol,
      kuikaId: latestRendererChild.id
    } as unknown as IApplyPropertyGridChangesToDisplayParameters;
    applyPropertyGridChange(context.sd, rendererParams, ESdChangeReason.ChangedCustomFieldOnPropertyPanel);
  }

  if (
    parentDi.item.name === "TableDefaultColumn" &&
    secondChildId &&
    targetId === secondChildId &&
    latestParent?.children?.[0]
  ) {
    const firstChild = latestParent.children[0];
    const firstChildParams = {
      propertyName: firstChild.item.name === "Checkbox" ? "checked" : context.property.name,
      symbol,
      kuikaId: firstChild.id
    } as unknown as IApplyPropertyGridChangesToDisplayParameters;
    applyPropertyGridChange(context.sd, firstChildParams, ESdChangeReason.ChangedCustomFieldOnPropertyPanel);
  }

  return setDesignerPropertySymbol(
    context.sdInstance,
    {
      kuikaId: context.di.id!.toString(),
      propertyName: context.property.name,
      symbol,
      screenId: context.screenId
    },
    ESdChangeReason.ChangedCustomFieldOnPropertyPanel
  );
}

function updateSymbolValue(
  context: PropertyContext,
  key: string,
  value: unknown
): { success: true; message: string } | { error: string } {
  const symbol = _.cloneDeep(context.property.symbol || {});

  const stringValue = value as string | undefined;

  switch (key) {
    case "fieldToDisplay":
      symbol.fieldToDisplay = stringValue;
      break;

    case "fieldToUseAsKey":
      symbol.fieldToUseAsKey = stringValue;
      break;

    case "fieldToUseAsParent":
      symbol.fieldToUseAsParent = stringValue;
      break;

    case "fieldToUseAsChecked":
      symbol.fieldToUseAsChecked = stringValue;
      break;

    case "fieldToUseAsGroup":
      symbol.fieldToUseAsGroup = stringValue;
      break;

    case "fieldToUseAsTagColor":
      symbol.fieldToUseAsTagColor = stringValue;
      break;

    case "fieldToUseAsIcon":
      symbol.fieldToUseAsIcon = stringValue;
      break;

    case "fieldToUseAsColor":
      symbol.fieldToUseAsColor = stringValue;
      break;

    case "fieldsToUseAsColumns": {
      // The multiselect editor hands over an array; persist it comma-joined (KOA-13797).
      const nextColumns: string = Array.isArray(value)
        ? (value as string[]).filter(Boolean).join(",")
        : (stringValue ?? "");
      if (!nextColumns) {
        delete symbol.fieldsToUseAsColumns;
      } else {
        symbol.fieldsToUseAsColumns = nextColumns;
      }
      break;
    }

    case "fieldToLatitude":
      symbol.fieldToLatitude = stringValue;
      break;

    case "fieldToLongitude":
      symbol.fieldToLongitude = stringValue;
      break;

    case "chartDataLabel":
    case "chartTooltipFooterDataLabel":
    case "chartFirstDataField":
    case "chartDataDisplayLabel":
    case "chartTargetLineField":
    case "chartFirstDataBgColor":
    case "chartSecondDataBgColor":
    case "chartThirdDataBgColor":
    case "chartFirstDataLegendLabel":
    case "chartSecondDataLegendLabel":
    case "chartThirdDataLegendLabel":
      if (value === undefined || value === "") {
        delete (symbol as unknown as Record<string, unknown>)[key];
      } else {
        (symbol as unknown as Record<string, unknown>)[key] = value;
      }
      break;

    case "chartFirstDataLineStyle":
    case "chartSecondDataLineStyle":
    case "chartThirdDataLineStyle":
      // "solid" is the default, so it is stored as "no value" to keep legacy symbols
      // byte-clean and emit nothing in code generation.
      if (value === undefined || value === "" || value === "solid") {
        delete (symbol as unknown as Record<string, unknown>)[key];
      } else {
        (symbol as unknown as Record<string, unknown>)[key] = value;
      }
      break;

    case "chartSecondDataField":
      if (value === undefined || value === "") {
        if (!symbol.chartThirdDataField) {
          delete symbol.chartSecondDataField;
        } else {
          symbol.chartSecondDataField = symbol.chartThirdDataField;
          delete symbol.chartThirdDataField;
        }
      } else {
        symbol.chartSecondDataField = stringValue;
      }
      break;

    case "chartThirdDataField":
      if (value === undefined || value === "") {
        delete symbol.chartThirdDataField;
      } else {
        symbol.chartThirdDataField = stringValue;
      }
      break;

    case "chartMultiTooltipContentField": {
      const nextValue = Array.isArray(value) ? (value as string[]).filter(Boolean).join(",") : stringValue;
      if (!nextValue) {
        delete symbol.chartMultiTooltipContentField;
      } else {
        symbol.chartMultiTooltipContentField = nextValue;
      }
      break;
    }

    case "valuePlaceholderText": {
      const nextValue = typeof value === "string" ? value.trim() : stringValue;
      if (!nextValue) {
        delete symbol.valuePlaceholderText;
      } else {
        symbol.valuePlaceholderText = nextValue;
      }
      break;
    }

    case "fixedValue":
      if (context.property.name === "defaultDataField" || context.property.name === "rowKey") {
        applyDefaultFieldValue(symbol, value);
      } else {
        symbol.isFixed = true;
        // Complex (list/object) fixed values — SelectBox / MultiSelect /
        // AutoComplete / RadioGroup `options`, decorative-table `dataSource`,
        // pagination `sizeChangerOptions` — must persist as a JSON STRING, not
        // a live array. The runtime, code generation, and the manual property-
        // panel editor (data-key-text-field-list, which JSON.stringify()s on
        // write and JSON.parse()s on read) all expect the serialized form.
        // Programmatic callers (MCP ui_property.set_multiple_data_fields, the
        // Binder) hand us an already-parsed array; serialize it here so the
        // value matches the manual editor's contract instead of rendering an
        // empty dropdown. Scalars (string / number / bool) pass through
        // untouched, and null/undefined are left as-is (never stringified).
        symbol.fixedValue = value !== null && typeof value === "object" ? JSON.stringify(value) : value;
        // Fixed-options lists (SelectBox / MultiSelect / AutoComplete /
        // RadioGroup `options`) are ALWAYS the `[{key,text}]` shape produced by
        // the manual data-key-text editor. The runtime and React code generator
        // read each option's VALUE field from `fieldToUseAsKey` (→ SelectPk →
        // `datavaluefield`) and its DISPLAY label from `fieldToDisplay` (→
        // SelectBy → `datatextfield`). Without those two, the generated
        // <KSelectBox> renders an EMPTY dropdown even though `dataSource_<id>`
        // holds the array — the runtime doesn't know which field is the label.
        // The manual property-panel editor sets both; mirror it here so
        // programmatic callers (MCP ui_property.set_multiple_data_fields, the
        // Binder) produce the same metadata. Scoped to `options` so scalar
        // fixed values and decorative-table `dataSource` arrays (arbitrary
        // row fields) are untouched.
        if (context.property.name === "options") {
          symbol.fieldToUseAsKey = "key";
          symbol.fieldToDisplay = "text";
        }
        if (context.property.tagName === "sizeChangerOptions") {
          symbol.category = ESymbolCategory.fixed;
        }
      }
      break;

    default:
      if (key.startsWith("chartAdditionalSeries.")) {
        applyChartAdditionalSeriesField(symbol, key, value);
      } else if (key.includes(".")) {
        setNestedOptionValue(symbol, key, value);
      } else {
        (symbol as unknown as Record<string, unknown>)[key] = value;
      }
      break;
  }

  setCategoryForField(symbol, key);

  return setDesignerPropertySymbol(
    context.sdInstance,
    {
      kuikaId: context.di.id!.toString(),
      propertyName: context.property.name,
      symbol,
      screenId: context.screenId
    },
    ESdChangeReason.ChangedCustomFieldOnPropertyPanel
  );
}

interface DataEditorInput extends IScreenScopedInput {
  kuikaId?: string;
  propertyName?: string;
}

interface SetDataActionInput extends DataEditorInput {
  value?: string;
}

interface SetDataFieldInput extends DataEditorInput {
  key?: string;
  fieldKey?: string;
  editorKey?: string;
  value?: unknown;
  prePath?: string;
  targetId?: string | number;
  resetSymbol?: boolean;
}

export function getDesignerPropertyDataEditorState(
  sdInstance: ScreenDesignInstance,
  input: DataEditorInput
): DesignerPropertyDataEditorState | { success: false; error: string } {
  const context = resolvePropertyContext(sdInstance, input);
  if ("error" in context) {
    return { success: false, error: context.error };
  }

  const valuePropState = getValuePropEditorState(context);
  if (valuePropState) {
    return valuePropState;
  }

  const defaultDataFieldState = getDefaultDataFieldEditorState(context);
  if (defaultDataFieldState) {
    return defaultDataFieldState;
  }

  const rowKeyState = getRowKeyEditorState(context);
  if (rowKeyState) {
    return rowKeyState;
  }

  if (
    !TREE_ACTION_TOOL_TYPES.has(context.propertyToolType) &&
    !SIMPLE_ACTION_TOOL_TYPES.has(context.propertyToolType)
  ) {
    return {
      success: false,
      error: `Property ${context.property.name} on ${context.di.item.name} does not use the property-form data editor flow`
    };
  }

  const actionSelectorKind = TREE_ACTION_TOOL_TYPES.has(context.propertyToolType) ? "tree" : "select";
  const actionOptions =
    actionSelectorKind === "tree"
      ? getTreeActionOptions(context.sd, context.propertyToolType === "Options")
      : getSimpleActionOptions(context.sd);
  const actionFieldOptions = getActionFieldOptions(context);

  return {
    success: true,
    propertyToolType: context.propertyToolType,
    actionSelector: {
      kind: actionSelectorKind,
      value: getSelectedDataAction(context.sd, context.property.symbol),
      allowClear: true,
      options: actionOptions
    },
    editors: buildEditors(context, actionFieldOptions)
  };
}

export function setDesignerPropertyDataAction(
  sdInstance: ScreenDesignInstance,
  input: SetDataActionInput
):
  | { success: false; error: string }
  | { success: true; message: string; selectedAction?: string; propertyToolType: string; derivedRowKey?: string }
  | { error: string; selectedAction?: string; propertyToolType: string } {
  const context = resolvePropertyContext(sdInstance, input);
  if ("error" in context) {
    return { success: false, error: context.error };
  }

  const symbol = _.cloneDeep(context.property.symbol || {});
  const nextValue = input?.value;
  const previousActionDefinitionId = symbol.actionDefinitionId;
  // KOA-18179 — the symbol's shape BEFORE we touch it. `fixed → action` is just
  // as much a shape change as `action → other action`, but only the latter was
  // clearing the dependent fields, so the fixed-list column names survived onto
  // an action that has no such columns. See `deriveOptionFieldsFromReturnType`.
  const previousWasFixed: boolean = symbol.isFixed === true;
  const isOptionsProperty: boolean = context.property.isOptionsProp === true || context.property.name === "options";

  if (nextValue === "Fixed Option List") {
    symbol.fieldToDisplay = "Text";
    symbol.fieldToUseAsKey = "Key";
    symbol.isFixed = true;
    if (symbol.fixedValue === undefined || symbol.fixedValue === "") {
      symbol.fixedValue = "[]";
    }
    symbol.actionDefinitionId = undefined;
    symbol.actionDefinitionObjectPath = undefined;
    symbol.resultActionId = undefined;
  } else if (nextValue !== undefined && nextValue !== null && nextValue !== "") {
    const { action, actionDef, actionName } = resolveActionSelection(context.sd, nextValue);
    // Neither an on-screen instance nor a definition answers to this name: writing
    // it anyway leaves a dangling `actionDefinitionObjectPath` with no ids behind a
    // "success" — the component then renders its sample rows and nobody knows why.
    if (!action && !actionDef) {
      return { success: false, error: buildUnknownDataActionError(context.sd, nextValue) };
    }
    symbol.isFixed = false;
    symbol.actionDefinitionId = actionDef?.id;
    symbol.actionDefinitionObjectPath = actionName;
    symbol.resultActionId = action?.id;

    const actionChanged: boolean =
      previousActionDefinitionId !== undefined && symbol.actionDefinitionId !== previousActionDefinitionId;
    // KOA-18179 — clear on EITHER shape change. The `previousWasFixed` arm is the
    // fix: a component whose `options` still carried the fixed-list `key`/`text`
    // names got an action bound with `previousActionDefinitionId === undefined`,
    // so this guard never fired and the stale names shipped into the saved tree —
    // where project validation reported them as fields of the ACTION that "no
    // longer exist", blocking codegen/Preview.
    if (actionChanged || previousWasFixed) {
      clearDependentActionFields(symbol);
    }
    // Refill the option columns from the bound action's own schema so clearing
    // them does not leave an empty dropdown. Only when they are genuinely empty:
    // a caller that names the columns (set_datasource_bindings' dataTextField /
    // dataValueField, the property panel) sets them AFTER this call and wins.
    if (isOptionsProperty && symbol.fieldToDisplay === undefined && symbol.fieldToUseAsKey === undefined) {
      const derived = deriveOptionFieldsFromReturnType(actionDef?.returnTypeModel);
      if (derived.fieldToUseAsKey !== undefined) symbol.fieldToUseAsKey = derived.fieldToUseAsKey;
      if (derived.fieldToDisplay !== undefined) symbol.fieldToDisplay = derived.fieldToDisplay;
    }
  } else {
    // KOA-18560 — no action is left, so no column name can still be right. Wiping only the
    // tool type's own editors kept hidden columns (the `options` key/display this function
    // derives) that the NEXT action then inherited: the "action changed" guard above does not
    // fire when nothing was bound before.
    clearDependentActionFields(symbol);
    clearActionValuesForToolType(symbol, context.propertyToolType);
  }

  const result = setDesignerPropertySymbol(
    context.sdInstance,
    {
      kuikaId: context.di.id!.toString(),
      propertyName: context.property.name,
      symbol,
      screenId: context.screenId
    },
    ESdChangeReason.ChangedCustomFieldOnPropertyPanel
  );

  const isActionBound: boolean = !("error" in result) && symbol.isFixed === false && !!symbol.actionDefinitionId;
  const derivedRowKey: string | undefined = isActionBound ? syncDefaultRowKeyToDataSource(context, symbol) : undefined;

  return {
    ...result,
    selectedAction: nextValue,
    propertyToolType: context.propertyToolType,
    ...(derivedRowKey !== undefined ? { derivedRowKey } : {})
  };
}

interface DataFieldResult {
  success?: true | false;
  message?: string;
  error?: string;
  key?: string;
  propertyToolType?: string;
}

export async function setDesignerPropertyDataField(
  sdInstance: ScreenDesignInstance,
  input: SetDataFieldInput
): Promise<DataFieldResult> {
  const context = resolvePropertyContext(sdInstance, input);
  if ("error" in context) {
    return { success: false, error: context.error };
  }

  const key = input?.key || input?.fieldKey || input?.editorKey;
  const value = input?.value;
  const prePath = input?.prePath;

  if (!key) {
    return { success: false, error: "key is required" };
  }

  if (key === "tableLabelSourceType") {
    const symbol = _.cloneDeep(context.property.symbol || {});
    symbol.tableLabelSourceType = value as string | undefined;
    if (input?.resetSymbol !== false) {
      symbol.fieldToDisplay = undefined;
      symbol.fieldToUseAsKey = undefined;
      symbol.fieldName = undefined;
      symbol.currentType = undefined;
      symbol.description = "";
      symbol.label = "";
      symbol.fieldType = undefined;
      symbol.targetType = undefined;
      symbol.converterOfType = undefined;
    }

    return setDesignerPropertySymbol(
      context.sdInstance,
      {
        kuikaId: context.di.id!.toString(),
        propertyName: context.property.name,
        symbol,
        screenId: context.screenId
      },
      ESdChangeReason.ChangedCustomFieldOnPropertyPanel
    );
  }

  const chartColorSourceTypeToValueField: Record<string, string> = {
    chartFirstDataBgColorSourceType: "chartFirstDataBgColor",
    chartSecondDataBgColorSourceType: "chartSecondDataBgColor",
    chartThirdDataBgColorSourceType: "chartThirdDataBgColor"
  };
  const chartColorValueField: string | undefined = chartColorSourceTypeToValueField[key];
  if (chartColorValueField) {
    const symbol = _.cloneDeep(context.property.symbol || {});
    (symbol as unknown as Record<string, unknown>)[key] = value as string | undefined;
    // Switching Column<->Fixed must drop the stale value: a column name must not
    // leak out as a literal color, nor a hex string as a column reference.
    delete (symbol as unknown as Record<string, unknown>)[chartColorValueField];

    return setDesignerPropertySymbol(
      context.sdInstance,
      {
        kuikaId: context.di.id!.toString(),
        propertyName: context.property.name,
        symbol,
        screenId: context.screenId
      },
      ESdChangeReason.ChangedCustomFieldOnPropertyPanel
    );
  }

  if (key === "tableFieldBinding") {
    return applyTableFieldBinding(context, value as string | undefined, prePath);
  }

  if (key === "tableEditModeRenderer") {
    return applyTableEditModeRenderer(context, value as string | undefined, input?.targetId);
  }

  const result = updateSymbolValue(context, key, value);
  return {
    ...result,
    key,
    propertyToolType: context.propertyToolType
  };
}

/**
 * WHICH of the platform's writers owns a property — KOA-18431.
 *
 * The platform does not have one way to set a property. It has three, and which one applies is a
 * fact about the property and its place in the tree, not a preference:
 *
 *   - a DATA-EDITOR property (`dataSource`, `options`, a chart's series) is owned by the datasource
 *     picker, which binds an ACTION and then names which of its columns to read;
 *   - a VALUE property of a component standing inside a data repeater is owned by the column field
 *     picker, which writes a `[datafield:…]` row token plus the field's own type and definition;
 *   - everything else is owned by the ordinary symbol picker.
 *
 * Writing through the wrong one produces a property that LOOKS bound — the write succeeds, the
 * symbol is there — and is not: the data editor's own panel reads fields the symbol does not
 * carry, and code generation loses the field reference. KOA-17797 already paid for this once on
 * the AI's screen-binding path (`enrichTableFieldBindingSymbol` exists because of it); the task
 * screen composition path reproduced it by writing every property through the symbol writer.
 *
 * So the question is asked ONCE, here, and every caller routes on the answer.
 */
export type PropertyWriteKind =
  /** The datasource / options picker: bind an action, then name its columns. */
  | "data-action"
  /** The column field picker: a row field of the enclosing data repeater. */
  | "row-field"
  /** The ordinary symbol picker. */
  | "symbol";

export interface IPropertyWriteKindInput extends IScreenScopedInput {
  kuikaId?: string;
  propertyName?: string;
}

/**
 * Answer for one property, or the reason it cannot be answered.
 *
 * `row-field` is reported ONLY when the enclosing repeater's datasource actually resolves to a
 * field list: a value property with no repeater above it (a plain label on a screen) is an ordinary
 * symbol target, and claiming otherwise would route a perfectly good binding into a picker that has
 * nothing to offer it.
 */
export function resolvePropertyWriteKind(
  sdInstance: ScreenDesignInstance,
  input: IPropertyWriteKindInput
): PropertyWriteKind | { error: string } {
  const context = resolvePropertyContext(sdInstance, input);
  if ("error" in context) return { error: context.error };

  if (TREE_ACTION_TOOL_TYPES.has(context.propertyToolType) || SIMPLE_ACTION_TOOL_TYPES.has(context.propertyToolType)) {
    return "data-action";
  }

  // Same predicate the platform's own value-property editor uses to decide it is looking at a row
  // field (`getValuePropEditorState`), including the three styling bindings that are value-shaped
  // without carrying the flag.
  const property = context.property;
  const isValueShaped: boolean =
    property.isValueProp === true ||
    property.name === "dynamicFontColor" ||
    property.name === "dynamicBackGroundColor" ||
    property.name === "dynamicBgImage";
  if (!isValueShaped) return "symbol";

  return getTableFieldOptions(context).length > 0 ? "row-field" : "symbol";
}

/**
 * The fields the enclosing data repeater offers a cell, by the cell's own id.
 *
 * Exposed so a caller can REFUSE a row binding that names a field the datasource does not have —
 * rather than writing it and shipping an empty column — and so it can hand the picker the FIELD
 * rather than a name. The platform's own panel is a select list over exactly these, and it submits
 * the serialized field: the definition travels into the symbol, which is what the panel reads back
 * and what code generation resolves the binding through. A bare name resolves to the same field
 * but leaves that definition behind.
 */
export function listRowFields(
  sdInstance: ScreenDesignInstance,
  input: IPropertyWriteKindInput
): IKTypeField[] | { error: string } {
  const context = resolvePropertyContext(sdInstance, input);
  if ("error" in context) return { error: context.error };
  return getTableFieldOptions(context);
}

/** The same fields, named the way a refusal has to spell them (`prefix.name` when nested). */
export function rowFieldPath(field: IKTypeField): string {
  return field.prefix ? `${field.prefix}.${field.name}` : field.name;
}

/**
 * The fields a DATA HOST's own source returns — KOA-18431.
 *
 * `listRowFields` answers for a CELL, by walking up to the repeater above it. This answers for the
 * repeater ITSELF, from the source bound to the property named, so a caller can compare what the
 * list could show against what it does show. Same resolution the platform's own column picker
 * performs, so the names are the ones a binding may use.
 */
export function listDataHostFields(
  sdInstance: ScreenDesignInstance,
  input: IPropertyWriteKindInput
): IKTypeField[] | { error: string } {
  const context = resolvePropertyContext(sdInstance, input);
  if ("error" in context) return { error: context.error };
  const symbol: ISymbol | undefined = context.property.symbol;
  if (symbol === undefined) return [];
  return context.propertySdHelper.getActionFieldsBySymbol(symbol, false);
}

/**
 * The list properties of a CATALOG component whose OWN VALUE is picked from them — KOA-18431.
 *
 * Asked by definition, before any instance exists, so a placement can be refused instead of
 * shipping a control with nothing in it. Live `eacfcdb9`: a category filter was placed on two task
 * screens and wired to its own `onChange` read, and neither got a single option — nothing in that
 * application declared the category set, so the author wrote the half it could and the surface
 * accepted the half it could not.
 *
 * ## Both halves are the catalog's, and both have to be the RIGHT half
 *
 * The question is whether the component's own value is picked FROM the list. Two facts answer it,
 * and the catalog states each one:
 *
 *   - the list is the platform's GENERIC option list — the one its property editor routes to the
 *     `Options` tool (`isOptionProperty`). Every widget's own content list has its OWN flag and its
 *     OWN editor (a table's `dataSource`, a calendar's events, a menu's items, a tree's nodes, the
 *     chart, maps and kanban families), so the platform already separates content from choices;
 *   - and the component is one the platform WRAPS IN A FORM ITEM — the label-and-validation frame a
 *     person fills. A widget that lays itself out is not an input.
 *
 * Neither half stands alone, and that is what the first version of this got wrong: "any
 * option-family property AND any `isValueProp` one" refused every Table placed on a task screen
 * (live `51831bb5` — 74 composition lines, 46 refused, both approvers' lists gone). A Table carries
 * four value properties of its own state (the page it shows, that page's size, what is selected)
 * and its page-size changer carries a generic option list, so it satisfied both of those tests
 * while being exactly the content widget the rule was written to stay off. Measured against the
 * running catalog, the pair above keeps `AutoComplete`, `MultiSelect`, `RadioGroup` and `SelectBox`
 * and excludes all eight content and navigation widgets that carry a value property — `Table`,
 * both `Calendar`s, `Carousel`, `Chat`, `Directory`, `Maps`, `Menu` and `Tree`.
 *
 * `Tree` is the deliberate edge: its value IS a node the user selects, but its list is its own
 * content hierarchy (own flag, own editor) and the property holding that value is marked technical,
 * which the platform hides from the property surface. An unsourced tree renders empty where the
 * author can see it — the case this rule accepts, exactly as it accepts an unsourced Table.
 *
 * Never a list of component names — a new control gains the properties and this answers for it
 * unchanged.
 */
export function pickedValueListPropertiesOf(component: IComponent | undefined): string[] {
  if (component?.wrapWithFormItem !== true) return [];
  const properties: readonly IProperty[] = component.properties ?? [];
  const carriesPickedValue: boolean = properties.some((property: IProperty): boolean => property.isValueProp === true);
  if (!carriesPickedValue) return [];
  const names: string[] = [];
  for (const property of properties) {
    // The platform's own `isOptionProperty`: a widget's content list carries its own flag instead.
    if (property.isOptionsProp !== true) continue;
    const name: string = (property.name ?? "").trim();
    if (name.length > 0) names.push(name);
  }
  return names;
}
